import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, CheckCircle, Users, Building2, Building, UserPlus, MapPin, Edit2, Trash2, Plus, Search, Filter, Download, FileText, FileSpreadsheet, X, Image as ImageIcon, BarChart3, TrendingUp, Clock, Truck, Info, Upload, ChevronLeft, ChevronRight, Calendar, UserCog, Briefcase, AlertCircle, Wrench, Package, ShoppingCart, Calculator, Settings, Check } from "lucide-react";
import * as XLSX from 'xlsx';
import JSZip from 'jszip';
import { jsPDF } from 'jspdf';
import { autoTable } from 'jspdf-autotable';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell, Legend, ReferenceLine } from 'recharts';

// Tab Components
import { JobsTab, AccountTab, FinancialsTab, DumpingGroundsTab, AnalyticsTab, MaintenanceTab, ProjectsTab } from './tabs';

// ============================================================================
// CONFIGURATION & CONSTANTS
// ============================================================================
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const DATA_RETENTION_TEXT = "Delivery data is stored for 2 years";
const ITEMS_PER_PAGE = 500;
const IMAGES_PER_PAGE = 20;
const POLL_INTERVAL_MS = 10000;

// Chart colors for financial visualizations
const CHART_COLORS = ['#f97316', '#22c55e', '#3b82f6', '#eab308', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6'];

// All configurable tabs for manager permissions
const ALL_CONFIGURABLE_TABS = [
  { id: "deliveries", label: "Deliveries" },
  { id: "drivers", label: "Drivers" },
  { id: "sites", label: "Sites" },
  { id: "jobs", label: "Jobs" },
  { id: "account", label: "Account" },
  { id: "analytics", label: "Analytics" },
  { id: "financials", label: "Financials" },
  { id: "images", label: "Images" },
  { id: "maintenance", label: "Maintenance" },
  { id: "dumping", label: "Dumping Grounds" },
  { id: "projects", label: "Projects" },
];

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

// Format date as dd/mm/yyyy
const formatDateDMY = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  // Format in Singapore timezone (GMT+8)
  return date.toLocaleDateString('en-GB', { 
    timeZone: 'Asia/Singapore',
    day: '2-digit',
    month: '2-digit', 
    year: 'numeric'
  });
};

// Format date with time as dd/mm/yyyy HH:mm in Singapore timezone
const formatDateTimeDMY = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  // Format in Singapore timezone (GMT+8)
  const datePart = date.toLocaleDateString('en-GB', { 
    timeZone: 'Asia/Singapore',
    day: '2-digit',
    month: '2-digit', 
    year: 'numeric'
  });
  const timePart = date.toLocaleTimeString('en-GB', { 
    timeZone: 'Asia/Singapore',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
  return `${datePart} ${timePart}`;
};

// Get tomorrow's date in Singapore timezone (UTC+8)
const getTomorrowSGT = () => {
  const now = new Date();
  const sgtOffset = 8 * 60;
  const utc = now.getTime() + (now.getTimezoneOffset() * 60000);
  const sgtTime = new Date(utc + (sgtOffset * 60000));
  sgtTime.setDate(sgtTime.getDate() + 1);
  return sgtTime.toISOString().split('T')[0];
};

// Check if job is expired (past 19:30 SGT)
const isJobExpired = (job) => {
  if (job.status === 'completed') return false;
  const now = new Date();
  const sgtOffset = 8 * 60;
  const utc = now.getTime() + (now.getTimezoneOffset() * 60000);
  const sgtNow = new Date(utc + (sgtOffset * 60000));
  const [year, month, day] = job.assigned_date.split('-').map(Number);
  const cutoffDate = new Date(year, month - 1, day, 19, 30, 0);
  return sgtNow > cutoffDate;
};

// Get status color for jobs
const getStatusColor = (status, expired = false) => {
  if (expired) return 'bg-red-500/20 text-red-400 border-red-500/30';
  switch (status) {
    case 'pending': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
    case 'in_progress': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
    case 'completed': return 'bg-green-500/20 text-green-400 border-green-500/30';
    default: return 'bg-white/10 text-white/60 border-white/20';
  }
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================
export default function AdminDashboard({ user, onLogout }) {
  // --------------------------------------------------------------------------
  // STATE DECLARATIONS
  // --------------------------------------------------------------------------
  
  // Dashboard stats
  const [stats, setStats] = useState({
    total_sites: 0,
    completed_today: 0,
    total_drivers: 0,
  });
  
  // Core data
  const [allDeliveries, setAllDeliveries] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [sites, setSites] = useState([]);
  
  // UI state
  const [view, setView] = useState("deliveries");
  const [showCreateDriver, setShowCreateDriver] = useState(false);
  const [showCreateAdmin, setShowCreateAdmin] = useState(false);
  const [showCreateSite, setShowCreateSite] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [editingDriver, setEditingDriver] = useState(null);
  const [editingSite, setEditingSite] = useState(null);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [editingAccount, setEditingAccount] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'checkin_time', direction: 'desc' });
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterDriver, setFilterDriver] = useState("all");
  const [filterSite, setFilterSite] = useState("all");
  const [dateRange, setDateRange] = useState({ start: "", end: "" });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [driverSearchTerm, setDriverSearchTerm] = useState("");
  const [siteSearchTerm, setSiteSearchTerm] = useState("");
  const [adminSearchTerm, setAdminSearchTerm] = useState("");
  const [selectedDeliveries, setSelectedDeliveries] = useState([]); // For bulk selection
  const [newDriver, setNewDriver] = useState({
    username: "",
    password: "",
    full_name: "",
    truck_number: "",
    phone: "",
    role: "driver"
  });
  const [newAdmin, setNewAdmin] = useState({
    username: "",
    password: "",
    full_name: ""
  });
  const [newSite, setNewSite] = useState({
    name: "",
    address: "",
    customer_name: "",
    cost: "",
    sales: ""
  });
  
  // Managers state
  const [managers, setManagers] = useState([]);
  const [managerSearchTerm, setManagerSearchTerm] = useState("");
  const [showAddManager, setShowAddManager] = useState(false);
  const [newManager, setNewManager] = useState({ username: "", password: "", full_name: "" });
  const [editingManager, setEditingManager] = useState(null);
  const [editingPermissions, setEditingPermissions] = useState(null); // Manager permissions modal
  
  // Mechanics state
  const [mechanics, setMechanics] = useState([]);
  
  // Inventory state
  const [inventory, setInventory] = useState([]);
  const [lowStockItems, setLowStockItems] = useState([]);
  
  // Purchases state
  const [purchases, setPurchases] = useState([]);
  
  // Suppliers state
  const [suppliers, setSuppliers] = useState([]);
  
  // Repairs state
  const [repairs, setRepairs] = useState([]);
  
  // Jobs state
  const [jobs, setJobs] = useState([]);
  const [unfinishedJobs, setUnfinishedJobs] = useState({ total_unfinished: 0, by_driver: [], jobs: [] });
  const [showAddJob, setShowAddJob] = useState(false);
  const [newJob, setNewJob] = useState({ site_id: "", driver_id: "", assigned_date: getTomorrowSGT(), loads_required: "", notes: "", driverSearch: "", siteSearch: "", dumping_ground_id: "" });
  const [jobFilter, setJobFilter] = useState("unfinished"); // all, unfinished, completed, expired, archived
  const [archivedJobs, setArchivedJobs] = useState([]);
  const [editingJob, setEditingJob] = useState(null); // Job being edited
  
  // Analytics state
  const [analyticsOverview, setAnalyticsOverview] = useState(null);
  const [deliveriesByDate, setDeliveriesByDate] = useState([]);
  const [driverPerformance, setDriverPerformance] = useState([]);
  const [siteStats, setSiteStats] = useState([]);
  const [customerStats, setCustomerStats] = useState([]);
  const [siteFinancials, setSiteFinancials] = useState([]);
  const [financialsByDate, setFinancialsByDate] = useState([]);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsStartDate, setAnalyticsStartDate] = useState("");
  const [analyticsEndDate, setAnalyticsEndDate] = useState("");
  
  // Analytics filters state
  const [analyticsFilterDrivers, setAnalyticsFilterDrivers] = useState([]);
  const [analyticsFilterSites, setAnalyticsFilterSites] = useState([]);
  const [analyticsFilterCustomers, setAnalyticsFilterCustomers] = useState([]);
  const [showDriverFilter, setShowDriverFilter] = useState(false);
  const [showSiteFilter, setShowSiteFilter] = useState(false);
  const [showCustomerFilter, setShowCustomerFilter] = useState(false);
  
  // Site financials sorting state
  const [financialsSortColumn, setFinancialsSortColumn] = useState("profit_loss");
  const [financialsSortDirection, setFinancialsSortDirection] = useState("desc");
  
  // Note: Financials tab state is now managed by FinancialsTab component
  
  const [dataLoading, setDataLoading] = useState(true);
  const [deliveriesLoading, setDeliveriesLoading] = useState(false);
  const [totalDeliveries, setTotalDeliveries] = useState(0);
  const [serverPage, setServerPage] = useState(1);
  const [serverPages, setServerPages] = useState(1);
  const [importingDrivers, setImportingDrivers] = useState(false);
  const [importingSites, setImportingSites] = useState(false);
  const [importingDeliveries, setImportingDeliveries] = useState(false);
  
  // Images gallery state
  const [allImages, setAllImages] = useState([]);
  const [selectedImages, setSelectedImages] = useState([]);
  const [imagesPage, setImagesPage] = useState(1);
  const [imagesLoading, setImagesLoading] = useState(false);
  const IMAGES_PER_PAGE = 20;
  
  // Refs for file inputs
  const driverFileInputRef = useRef(null);
  const siteFileInputRef = useRef(null);
  const deliveryFileInputRef = useRef(null);

  const abortControllerRef = useRef(null);
  
  const fetchData = async (filterStartDate = "", filterEndDate = "", pageNum = 1, driverId = "all", siteId = "all", statusFilter = "all", searchQuery = "") => {
    // Cancel any pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Create new abort controller for this request
    const controller = new AbortController();
    abortControllerRef.current = controller;
    
    try {
      // Load stats first (fast) to show something quickly
      const statsRes = await axios.get(`${API}/dashboard/stats`);
      setStats(statsRes.data);
      setDataLoading(false); // Show UI immediately with stats
      
      // Fetch drivers separately to ensure it succeeds
      try {
        const driversRes = await axios.get(`${API}/admin/drivers`);
        setDrivers(driversRes.data || []);
      } catch (driverError) {
        console.error('Failed to load drivers:', driverError);
      }
      
      // Fetch sites separately
      try {
        const sitesRes = await axios.get(`${API}/sites`);
        setSites(sitesRes.data || []);
      } catch (siteError) {
        console.error('Failed to load sites:', siteError);
      }
      
      // Fetch deliveries with all filters
      setDeliveriesLoading(true);
      try {
        let deliveryUrl = `${API}/deliveries?page=${pageNum}&limit=500`;
        if (filterStartDate) {
          deliveryUrl += `&start_date=${filterStartDate}`;
        }
        if (filterEndDate) {
          deliveryUrl += `&end_date=${filterEndDate}`;
        }
        if (driverId && driverId !== "all") {
          deliveryUrl += `&driver_id=${driverId}`;
        }
        if (siteId && siteId !== "all") {
          deliveryUrl += `&site_id=${siteId}`;
        }
        if (statusFilter && statusFilter !== "all") {
          deliveryUrl += `&status=${statusFilter}`;
        }
        if (searchQuery && searchQuery.trim()) {
          deliveryUrl += `&search=${encodeURIComponent(searchQuery.trim())}`;
        }
        const allRes = await axios.get(deliveryUrl, { signal: controller.signal });
        
        // Only update state if this request wasn't aborted
        if (!controller.signal.aborted) {
          // Handle new paginated response format
          if (allRes.data.data) {
            setAllDeliveries(allRes.data.data);
            setTotalDeliveries(allRes.data.total);
            setServerPages(allRes.data.pages);
          } else {
            setAllDeliveries(allRes.data);
          }
          setDeliveriesLoading(false);
        }
      } catch (deliveriesError) {
        if (axios.isCancel(deliveriesError) || deliveriesError.name === 'CanceledError') {
          // Request was cancelled - this is expected when filters change rapidly
        } else {
          console.error('Failed to load deliveries:', deliveriesError);
          setDeliveriesLoading(false);
        }
      }
    } catch (error) {
      if (axios.isCancel(error) || error.name === 'CanceledError') {
        // Request was cancelled - this is expected
      } else {
        console.error("Error fetching data:", error);
        setDataLoading(false);
      }
    }
  };

  useEffect(() => {
    // Fetch immediately with current values including filters
    fetchData(dateRange.start, dateRange.end, serverPage, filterDriver, filterSite, filterStatus, searchTerm);
    
    // Poll every 30 seconds for real-time updates
    const interval = setInterval(() => {
      fetchData(dateRange.start, dateRange.end, serverPage, filterDriver, filterSite, filterStatus, searchTerm);
    }, 30000);
    
    return () => {
      clearInterval(interval);
    };
  }, [serverPage, dateRange.start, dateRange.end, filterDriver, filterSite, filterStatus, searchTerm]); // Re-fetch when any filter changes

  useEffect(() => {
    if (view === "analytics") {
      fetchAnalytics();
    }
  }, [view]);

  // Fetch admins when admins tab is selected
  useEffect(() => {
    if (view === "admins" && user?.role === "admin") {
      fetchAdmins();
    }
  }, [view, user?.role]);

  // Fetch managers when managers tab is selected
  useEffect(() => {
    if (view === "managers") {
      fetchManagers();
    }
  }, [view]);

  // Fetch jobs when jobs tab is selected
  useEffect(() => {
    if (view === "jobs") {
      fetchJobs();
      fetchUnfinishedJobs();
    }
  }, [view]);

  // Fetch managers when managers tab is selected
  useEffect(() => {
    if (view === "managers") {
      fetchManagers();
    }
  }, [view]);

  // Fetch data when maintenance tab is selected
  useEffect(() => {
    if (view === "maintenance") {
      // Components now handle their own data fetching
    }
  }, [view]);

  // Note: Financials tab is now handled by FinancialsTab component which manages its own data fetching

  const fetchAdmins = async () => {
    try {
      const response = await axios.get(`${API}/admin/admins`);
      setAdmins(response.data);
    } catch (error) {
      console.error("Error fetching admins:", error);
    }
  };

  const fetchManagers = async () => {
    try {
      const response = await axios.get(`${API}/admin/managers`);
      setManagers(response.data);
    } catch (error) {
      console.error("Error fetching managers:", error);
    }
  };

  // Fetch mechanics
  const fetchMechanics = async () => {
    try {
      const response = await axios.get(`${API}/mechanics`);
      setMechanics(response.data || []);
    } catch (error) {
      console.error("Error fetching mechanics:", error);
    }
  };

  // Fetch inventory
  const fetchInventory = async () => {
    try {
      const [itemsRes, lowStockRes] = await Promise.all([
        axios.get(`${API}/inventory`),
        axios.get(`${API}/inventory/low-stock`)
      ]);
      setInventory(itemsRes.data || []);
      setLowStockItems(lowStockRes.data || []);
    } catch (error) {
      console.error("Error fetching inventory:", error);
    }
  };

  // Fetch purchases
  const fetchPurchases = async () => {
    try {
      const response = await axios.get(`${API}/purchases`);
      setPurchases(response.data || []);
    } catch (error) {
      console.error("Error fetching purchases:", error);
    }
  };

  // Fetch suppliers
  const fetchSuppliers = async () => {
    try {
      const response = await axios.get(`${API}/suppliers`);
      setSuppliers(response.data || []);
    } catch (error) {
      console.error("Error fetching suppliers:", error);
    }
  };

  // Fetch repairs
  const fetchRepairs = async () => {
    try {
      const response = await axios.get(`${API}/repairs`);
      setRepairs(response.data || []);
    } catch (error) {
      console.error("Error fetching repairs:", error);
    }
  };

  // Note: fetchFinancials has been moved to FinancialsTab component

  const fetchAnalytics = async (startDate = analyticsStartDate, endDate = analyticsEndDate, filterDrivers = analyticsFilterDrivers, filterSites = analyticsFilterSites, filterCustomers = analyticsFilterCustomers) => {
    setAnalyticsLoading(true);
    try {
      // Build query params for date filtering and filters
      const buildQueryString = () => {
        const params = new URLSearchParams();
        if (startDate) params.append('start_date', startDate);
        if (endDate) params.append('end_date', endDate);
        if (filterDrivers.length > 0) params.append('driver_ids', filterDrivers.join(','));
        if (filterSites.length > 0) params.append('site_ids', filterSites.join(','));
        if (filterCustomers.length > 0) params.append('customers', filterCustomers.join(','));
        return params.toString() ? `?${params.toString()}` : '';
      };
      const queryString = buildQueryString();
      
      // Fetch analytics in sequence with error handling for each
      const overviewRes = await axios.get(`${API}/analytics/overview${queryString}`).catch(() => ({ data: null }));
      const dateRes = await axios.get(`${API}/analytics/deliveries-by-date?days=30${startDate ? `&start_date=${startDate}` : ''}${endDate ? `&end_date=${endDate}` : ''}${filterDrivers.length > 0 ? `&driver_ids=${filterDrivers.join(',')}` : ''}${filterSites.length > 0 ? `&site_ids=${filterSites.join(',')}` : ''}${filterCustomers.length > 0 ? `&customers=${filterCustomers.join(',')}` : ''}`).catch(() => ({ data: [] }));
      const driverRes = await axios.get(`${API}/analytics/driver-performance${queryString}`).catch(() => ({ data: [] }));
      const siteRes = await axios.get(`${API}/analytics/site-stats${queryString}`).catch(() => ({ data: [] }));
      const customerRes = await axios.get(`${API}/analytics/customer-stats${queryString}`).catch(() => ({ data: [] }));
      
      // Fetch site financials (admin only) - will return 403 for managers
      const financialsRes = await axios.get(`${API}/analytics/site-financials${queryString}`).catch(() => ({ data: [] }));
      
      // Fetch financials by date (admin only)
      const financialsByDateRes = await axios.get(`${API}/analytics/financials-by-date?days=30${startDate ? `&start_date=${startDate}` : ''}${endDate ? `&end_date=${endDate}` : ''}${filterDrivers.length > 0 ? `&driver_ids=${filterDrivers.join(',')}` : ''}${filterSites.length > 0 ? `&site_ids=${filterSites.join(',')}` : ''}${filterCustomers.length > 0 ? `&customers=${filterCustomers.join(',')}` : ''}`).catch(() => ({ data: [] }));
      
      if (overviewRes.data) setAnalyticsOverview(overviewRes.data);
      setDeliveriesByDate(dateRes.data || []);
      setDriverPerformance(driverRes.data || []);
      setSiteStats(siteRes.data || []);
      setCustomerStats(customerRes.data || []);
      setSiteFinancials(financialsRes.data || []);
      setFinancialsByDate(financialsByDateRes.data || []);
    } catch (error) {
      console.error("Error fetching analytics:", error);
      toast.error("Failed to load analytics");
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const handleCreateDriver = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/drivers`, newDriver);
      toast.success("Driver created successfully!");
      setShowCreateDriver(false);
      setNewDriver({
        username: "",
        password: "",
        full_name: "",
        truck_number: "",
        phone: "",
        role: "driver"
      });
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to create driver"));
    }
  };

  // Safely extract an error message from an axios error response.
  // FastAPI/Pydantic 422 responses return `detail` as an array of objects
  // (e.g. [{type, loc, msg, ...}]). Passing that array to toast.error()
  // makes React try to render it as a child and crashes the entire dashboard.
  const getErrorMessage = (error, fallback) => {
    const detail = error?.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      return detail
        .map(d => (typeof d === 'string' ? d : (d?.msg || JSON.stringify(d))))
        .join('; ');
    }
    if (detail && typeof detail === 'object') return detail.msg || JSON.stringify(detail);
    return error?.message || fallback;
  };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/admins`, { ...newAdmin, role: "admin" });
      toast.success("Admin created successfully!");
      setShowCreateAdmin(false);
      setNewAdmin({
        username: "",
        password: "",
        full_name: ""
      });
      fetchAdmins();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to create admin"));
    }
  };

  const handleDeleteAdmin = async (adminId) => {
    if (!window.confirm("Are you sure you want to delete this admin?")) return;
    try {
      await axios.delete(`${API}/admin/admins/${adminId}`);
      toast.success("Admin deleted successfully!");
      fetchAdmins();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to delete admin"));
    }
  };

  const handleUpdateAdmin = async (adminId, updateData) => {
    try {
      await axios.patch(`${API}/admin/admins/${adminId}`, updateData);
      toast.success("Admin updated successfully!");
      setEditingAdmin(null);
      fetchAdmins();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to update admin"));
    }
  };

  // Manager CRUD handlers
  const handleCreateManager = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/managers`, { ...newManager, role: "manager" });
      toast.success("Manager created successfully!");
      setShowAddManager(false);
      setNewManager({ username: "", password: "", full_name: "" });
      fetchManagers();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to create manager"));
    }
  };

  const handleUpdateManager = async (managerId, updateData) => {
    try {
      await axios.patch(`${API}/admin/managers/${managerId}`, updateData);
      toast.success("Manager updated successfully!");
      setEditingManager(null);
      fetchManagers();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to update manager"));
    }
  };

  const handleDeleteManager = async (managerId) => {
    if (!window.confirm("Are you sure you want to delete this manager?")) return;
    try {
      await axios.delete(`${API}/admin/managers/${managerId}`);
      toast.success("Manager deleted successfully!");
      fetchManagers();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to delete manager"));
    }
  };

  // Update manager permissions (allowed tabs)
  const handleUpdatePermissions = async (managerId, allowedTabs) => {
    try {
      await axios.patch(`${API}/admin/managers/${managerId}/permissions`, { allowed_tabs: allowedTabs });
      toast.success("Manager permissions updated!");
      fetchManagers();
      setEditingPermissions(null);
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to update permissions"));
    }
  };

  // Filtered managers for search
  const filteredManagers = managers.filter(manager =>
    manager.full_name?.toLowerCase().includes(managerSearchTerm.toLowerCase()) ||
    manager.username?.toLowerCase().includes(managerSearchTerm.toLowerCase())
  );

  // Check if current user (manager) has access to a specific tab
  const hasTabAccess = (tabId) => {
    if (user?.role === "admin") return true;
    if (user?.role === "manager") {
      const allowedTabs = user?.allowed_tabs || ["deliveries", "drivers", "sites", "jobs", "account"];
      return allowedTabs.includes(tabId);
    }
    return false;
  };

  // Jobs functions
  const fetchJobs = async () => {
    try {
      const response = await axios.get(`${API}/admin/jobs`);
      setJobs(response.data);
    } catch (error) {
      console.error("Error fetching jobs:", error);
    }
  };

  const fetchUnfinishedJobs = async () => {
    try {
      const response = await axios.get(`${API}/admin/jobs/unfinished`);
      setUnfinishedJobs(response.data);
    } catch (error) {
      console.error("Error fetching unfinished jobs:", error);
    }
  };

  const handleCreateJob = async (e) => {
    e.preventDefault();
    try {
      // Prepare job data - only include loads_required if specified
      const jobData = {
        site_id: newJob.site_id,
        driver_id: newJob.driver_id,
        assigned_date: newJob.assigned_date,
        notes: newJob.notes
      };
      if (newJob.loads_required && newJob.loads_required !== "") {
        jobData.loads_required = parseInt(newJob.loads_required);
      }
      if (newJob.dumping_ground_id) {
        jobData.dumping_ground_id = newJob.dumping_ground_id;
      }
      await axios.post(`${API}/admin/jobs`, jobData);
      toast.success("Job created successfully!");
      setShowAddJob(false);
      setNewJob({ site_id: "", driver_id: "", assigned_date: getTomorrowSGT(), loads_required: "", notes: "", driverSearch: "", siteSearch: "", dumping_ground_id: "" });
      fetchJobs();
      fetchUnfinishedJobs();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to create job"));
    }
  };

  const handleUpdateJobStatus = async (jobId, newStatus) => {
    try {
      await axios.patch(`${API}/admin/jobs/${jobId}`, { status: newStatus });
      toast.success("Job updated successfully!");
      fetchJobs();
      fetchUnfinishedJobs();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to update job"));
    }
  };

  const handleDeleteJob = async (jobId) => {
    if (!window.confirm("Are you sure you want to delete this job?")) return;
    try {
      await axios.delete(`${API}/admin/jobs/${jobId}`);
      toast.success("Job deleted successfully!");
      fetchJobs();
      fetchUnfinishedJobs();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to delete job"));
    }
  };

  // Open edit modal with job data
  const handleEditJob = (job) => {
    const driver = drivers.find(d => d.id === job.driver_id);
    const site = sites.find(s => s.id === job.site_id);
    setEditingJob({
      ...job,
      driverSearch: driver ? `${driver.full_name} (${driver.truck_number || 'N/A'})` : job.driver_name,
      siteSearch: site ? `${site.name} (${site.customer_name || 'N/A'})` : job.site_name
    });
  };

  // Save edited job
  const handleSaveEditJob = async () => {
    if (!editingJob.driver_id || !editingJob.site_id || !editingJob.assigned_date) {
      toast.error("Please fill in all required fields");
      return;
    }
    try {
      const updateData = {
        driver_id: editingJob.driver_id,
        site_id: editingJob.site_id,
        assigned_date: editingJob.assigned_date,
        notes: editingJob.notes || ""
      };
      if (editingJob.loads_required && editingJob.loads_required !== "") {
        updateData.loads_required = parseInt(editingJob.loads_required);
      }
      await axios.patch(`${API}/admin/jobs/${editingJob.id}`, updateData);
      toast.success("Job updated successfully!");
      setEditingJob(null);
      fetchJobs();
      fetchUnfinishedJobs();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to update job"));
    }
  };

  const filteredJobs = jobFilter === 'all' 
    ? jobs 
    : jobFilter === 'unfinished' 
      ? jobs.filter(j => j.status !== 'completed' && !isJobExpired(j))
      : jobFilter === 'expired'
        ? jobs.filter(j => j.status !== 'completed' && isJobExpired(j))
        : jobFilter === 'archived'
          ? archivedJobs
          : jobs.filter(j => j.status === 'completed');

  // Count expired jobs
  const expiredJobsCount = jobs.filter(j => j.status !== 'completed' && isJobExpired(j)).length;

  const handleArchiveJob = async (jobId) => {
    try {
      await axios.patch(`${API}/admin/jobs/${jobId}/archive`);
      toast.success("Job archived");
      fetchJobs();
      fetchUnfinishedJobs();
      if (jobFilter === 'archived') fetchArchivedJobs();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to archive job"));
    }
  };

  const handleUnarchiveJob = async (jobId) => {
    try {
      await axios.patch(`${API}/admin/jobs/${jobId}/unarchive`);
      toast.success("Job restored");
      fetchArchivedJobs();
      fetchJobs();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to restore job"));
    }
  };

  const handleArchiveAllExpired = async () => {
    if (!window.confirm(`Archive all ${expiredJobsCount} expired job(s)? They can be restored later from the Archived tab.`)) return;
    try {
      const res = await axios.post(`${API}/admin/jobs/archive-expired`);
      toast.success(`${res.data.archived} expired job(s) archived`);
      fetchJobs();
      fetchUnfinishedJobs();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to archive expired jobs"));
    }
  };

  const fetchArchivedJobs = async () => {
    try {
      const res = await axios.get(`${API}/admin/jobs`, { params: { only_archived: true } });
      setArchivedJobs(res.data || []);
    } catch (_) {
      setArchivedJobs([]);
    }
  };

  const handleUpdateDriver = async (driverId, updateData) => {
    try {
      await axios.patch(`${API}/admin/drivers/${driverId}`, updateData);
      toast.success("Driver updated successfully!");
      setEditingDriver(null);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to update driver"));
    }
  };

  const handleAssignSites = async (driverId, siteIds) => {
    try {
      await axios.patch(`${API}/admin/drivers/${driverId}/assign-sites`, {
        driver_id: driverId,
        site_ids: siteIds
      });
      toast.success("Sites assigned successfully!");
      fetchData();
      setSelectedDriver(null);
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to assign sites"));
    }
  };

  const handleDeleteDriver = async (driverId) => {
    if (!window.confirm("Are you sure you want to delete this driver? This action cannot be undone.")) return;
    try {
      await axios.delete(`${API}/admin/drivers/${driverId}`);
      toast.success("Driver deleted successfully!");
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to delete driver"));
    }
  };

  const handleDeleteDelivery = async (deliveryId) => {
    if (user.role !== 'admin') {
      toast.error("Only administrators can delete deliveries");
      return;
    }
    if (!window.confirm("Are you sure you want to delete this delivery? This action cannot be undone.")) return;
    if (!window.confirm("Please confirm again: This will permanently delete the delivery record and any associated photo.")) return;
    try {
      await axios.delete(`${API}/deliveries/${deliveryId}`);
      toast.success("Delivery deleted successfully!");
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to delete delivery"));
    }
  };

  const handleBulkDeleteDeliveries = async () => {
    if (user.role !== 'admin') {
      toast.error("Only administrators can delete deliveries");
      return;
    }
    if (selectedDeliveries.length === 0) {
      toast.error("No deliveries selected");
      return;
    }
    if (!window.confirm(`Are you sure you want to delete ${selectedDeliveries.length} delivery record(s)? This action cannot be undone.`)) return;
    if (!window.confirm(`Please confirm again: This will permanently delete ${selectedDeliveries.length} delivery record(s) and any associated photos.`)) return;
    
    let successCount = 0;
    let failCount = 0;
    
    for (const deliveryId of selectedDeliveries) {
      try {
        await axios.delete(`${API}/deliveries/${deliveryId}`);
        successCount++;
      } catch (error) {
        console.error(`Failed to delete delivery ${deliveryId}:`, error);
        failCount++;
      }
    }
    
    if (successCount > 0) {
      toast.success(`Successfully deleted ${successCount} delivery record(s)`);
    }
    if (failCount > 0) {
      toast.error(`Failed to delete ${failCount} delivery record(s)`);
    }
    
    setSelectedDeliveries([]);
    fetchData();
  };

  const toggleDeliverySelection = (deliveryId) => {
    setSelectedDeliveries(prev => 
      prev.includes(deliveryId) 
        ? prev.filter(id => id !== deliveryId)
        : [...prev, deliveryId]
    );
  };

  const toggleSelectAllDeliveries = () => {
    if (selectedDeliveries.length === paginatedDeliveries.length) {
      setSelectedDeliveries([]);
    } else {
      setSelectedDeliveries(paginatedDeliveries.map(d => d.id));
    }
  };

  const handleCreateSite = async (e) => {
    e.preventDefault();
    try {
      // Prepare site data with cost/sales as numbers
      const siteData = {
        ...newSite,
        cost: newSite.cost ? parseFloat(newSite.cost) : null,
        sales: newSite.sales ? parseFloat(newSite.sales) : null
      };
      await axios.post(`${API}/admin/sites`, siteData);
      toast.success("Site created successfully!");
      setShowCreateSite(false);
      setNewSite({ name: "", address: "", customer_name: "", cost: "", sales: "" });
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to create site"));
    }
  };

  const handleUpdateSite = async (siteId, updateData) => {
    try {
      await axios.patch(`${API}/admin/sites/${siteId}`, updateData);
      toast.success("Site updated successfully!");
      setEditingSite(null);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to update site"));
    }
  };

  const handleDeleteSite = async (siteId) => {
    if (!window.confirm("Are you sure you want to delete this site?")) return;
    try {
      await axios.delete(`${API}/admin/sites/${siteId}`);
      toast.success("Site deleted successfully!");
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to delete site"));
    }
  };

  const handleUpdateAccount = async (updateData) => {
    try {
      const response = await axios.patch(`${API}/admin/account`, updateData);
      toast.success("Account updated successfully!");
      
      // Update local user state if username changed
      if (updateData.username) {
        const updatedUser = { ...user, username: updateData.username, full_name: updateData.full_name || user.full_name };
        localStorage.setItem("user", JSON.stringify(updatedUser));
      }
      
      setEditingAccount(false);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to update account"));
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedDeliveries = () => {
    if (!sortConfig.key) return allDeliveries;
    
    return [...allDeliveries].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];
      
      // Handle different data types
      if (sortConfig.key === 'checkin_time' || sortConfig.key === 'checkout_time') {
        aValue = aValue ? new Date(aValue).getTime() : 0;
        bValue = bValue ? new Date(bValue).getTime() : 0;
      } else if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue ? bValue.toLowerCase() : '';
      }
      
      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  const sortedDeliveries = getSortedDeliveries();

  const getFilteredDeliveries = () => {
    // All filtering is now done server-side
    // Just return sorted deliveries
    return sortedDeliveries;
  };

  const filteredDeliveries = getFilteredDeliveries();
  
  // Pagination for deliveries - now based on server pagination
  // Client-side pagination only used when no filters and showing all server data
  const totalPages = Math.ceil(filteredDeliveries.length / ITEMS_PER_PAGE);
  const paginatedDeliveries = filteredDeliveries.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );
  
  // Reset server page to 1 when filters change (but not when just the page changes)
  useEffect(() => {
    setServerPage(1);
    setSelectedDeliveries([]);
  }, [filterStatus, filterDriver, filterSite, searchTerm, dateRange]);

  // Clear selection when page changes
  useEffect(() => {
    setSelectedDeliveries([]);
  }, [serverPage]);

  // Filtered drivers for search
  const filteredDrivers = drivers.filter(driver => {
    const term = driverSearchTerm.toLowerCase();
    return (
      (driver.full_name || '').toLowerCase().includes(term) ||
      (driver.username || '').toLowerCase().includes(term) ||
      (driver.truck_number || '').toLowerCase().includes(term)
    );
  });

  // Filtered sites for search
  const filteredSites = sites.filter(site => {
    const term = siteSearchTerm.toLowerCase();
    return (
      (site.name || '').toLowerCase().includes(term) ||
      (site.address || '').toLowerCase().includes(term) ||
      (site.customer_name || '').toLowerCase().includes(term)
    );
  });

  // Filtered admins for search
  const filteredAdmins = admins.filter(admin => {
    const term = adminSearchTerm.toLowerCase();
    return (
      (admin.full_name || '').toLowerCase().includes(term) ||
      (admin.username || '').toLowerCase().includes(term)
    );
  });

  const exportToCSV = () => {
    const headers = ["Customer", "Site", "Driver", "Truck", "Check-in", "Check-out", "GPS Location", "Status"];
    const rows = filteredDeliveries.map(d => [
      d.customer_name || "-",
      d.site_name,
      d.driver_name,
      d.truck_number,
      formatDateTimeDMY(d.checkin_time),
      d.checkout_time ? formatDateTimeDMY(d.checkout_time) : "-",
      d.checkin_location ? `${d.checkin_location.lat}, ${d.checkin_location.lng}` : "-",
      d.status.toUpperCase()
    ]);

    const csv = [headers, ...rows].map(row => row.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `deliveries_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success("CSV exported successfully!");
  };

  const exportToExcel = () => {
    const data = filteredDeliveries.map(d => ({
      Customer: d.customer_name || "-",
      Site: d.site_name,
      Driver: d.driver_name,
      Truck: d.truck_number,
      "Check-in": formatDateTimeDMY(d.checkin_time),
      "Check-out": d.checkout_time ? formatDateTimeDMY(d.checkout_time) : "-",
      "GPS Location": d.checkin_location ? `${d.checkin_location.lat}, ${d.checkin_location.lng}` : "-",
      Status: d.status.toUpperCase()
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Deliveries");
    XLSX.writeFile(wb, `deliveries_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success("Excel file exported successfully!");
  };

  const exportDriversToExcel = () => {
    const data = filteredDrivers.map(d => ({
      "Full Name": d.full_name,
      "Username": d.username,
      "Truck Number": d.truck_number || "-",
      "Assigned Sites": d.assigned_sites?.length || 0
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Drivers");
    XLSX.writeFile(wb, `drivers_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success("Drivers exported successfully!");
  };

  const exportSitesToExcel = () => {
    const data = filteredSites.map(s => ({
      "Customer Name": s.customer_name || "-",
      "Site Name": s.name,
      "Address": s.address
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sites");
    XLSX.writeFile(wb, `sites_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success("Sites exported successfully!");
  };

  // Analytics Export Functions
  const exportAnalyticsToExcel = () => {
    const wb = XLSX.utils.book_new();
    const dateRange = analyticsStartDate && analyticsEndDate 
      ? `${analyticsStartDate}_to_${analyticsEndDate}` 
      : new Date().toISOString().split('T')[0];

    // Overview sheet
    if (analyticsOverview) {
      const overviewData = [{
        "Metric": "Total Deliveries",
        "Value": analyticsOverview.total_deliveries
      }, {
        "Metric": "Completion Rate (%)",
        "Value": analyticsOverview.completion_rate
      }, {
        "Metric": "Average Delivery Time (min)",
        "Value": analyticsOverview.avg_delivery_time_minutes
      }, {
        "Metric": "Active Deliveries",
        "Value": analyticsOverview.active_deliveries
      }];
      const overviewSheet = XLSX.utils.json_to_sheet(overviewData);
      XLSX.utils.book_append_sheet(wb, overviewSheet, "Overview");
    }

    // Driver Performance sheet
    if (driverPerformance.length > 0) {
      const driverData = driverPerformance.map(d => ({
        "Driver Name": d.driver_name,
        "Completed Deliveries": d.completed_deliveries,
        "Avg Delivery Time (min)": d.avg_delivery_time_minutes
      }));
      const driverSheet = XLSX.utils.json_to_sheet(driverData);
      XLSX.utils.book_append_sheet(wb, driverSheet, "Driver Performance");
    }

    // Site Stats sheet
    if (siteStats.length > 0) {
      const siteData = siteStats.map(s => ({
        "Site Name": s.site_name,
        "Total Deliveries": s.total_deliveries,
        "Completed": s.completed_deliveries || 0
      }));
      const siteSheet = XLSX.utils.json_to_sheet(siteData);
      XLSX.utils.book_append_sheet(wb, siteSheet, "Site Stats");
    }

    // Customer Stats sheet
    if (customerStats.length > 0) {
      const customerData = customerStats.map(c => ({
        "Customer Name": c.customer_name,
        "Total Deliveries": c.total_deliveries,
        "Completed Deliveries": c.completed_deliveries
      }));
      const customerSheet = XLSX.utils.json_to_sheet(customerData);
      XLSX.utils.book_append_sheet(wb, customerSheet, "Customer Stats");
    }

    // Deliveries by Date sheet
    if (deliveriesByDate.length > 0) {
      const dateData = deliveriesByDate.map(d => ({
        "Date": d.date,
        "Total": d.total,
        "Completed": d.completed
      }));
      const dateSheet = XLSX.utils.json_to_sheet(dateData);
      XLSX.utils.book_append_sheet(wb, dateSheet, "Daily Deliveries");
    }

    // Site Financials removed - now in dedicated Financials tab

    XLSX.writeFile(wb, `analytics_report_${dateRange}.xlsx`);
    toast.success("Analytics exported to Excel!");
  };

  const exportAnalyticsToPDF = () => {
    try {
      const doc = new jsPDF();
      const dateRangeText = analyticsStartDate && analyticsEndDate 
        ? `${analyticsStartDate} to ${analyticsEndDate}` 
        : "All Time";
      
      // Title
      doc.setFontSize(18);
      doc.text("Unity Delivery Tracker - Analytics Report", 14, 22);
      doc.setFontSize(10);
      doc.text(`Date Range: ${dateRangeText}`, 14, 30);
      doc.text(`Generated: ${formatDateTimeDMY(new Date())}`, 14, 36);

      let yPos = 46;

      // Overview Section
      if (analyticsOverview) {
        doc.setFontSize(14);
        doc.text("Overview", 14, yPos);
        yPos += 8;
        
        autoTable(doc, {
          startY: yPos,
          head: [["Metric", "Value"]],
          body: [
            ["Total Deliveries", String(analyticsOverview.total_deliveries || 0)],
            ["Completion Rate", `${analyticsOverview.completion_rate || 0}%`],
            ["Avg Delivery Time", `${analyticsOverview.avg_delivery_time_minutes || 0} min`],
            ["Active Deliveries", String(analyticsOverview.active_deliveries || 0)]
          ],
          theme: 'striped',
          headStyles: { fillColor: [249, 115, 22] }
        });
        yPos = doc.lastAutoTable ? doc.lastAutoTable.finalY + 12 : yPos + 50;
      }

      // Driver Performance Section
      if (driverPerformance && driverPerformance.length > 0) {
        doc.setFontSize(14);
        doc.text("Top Drivers", 14, yPos);
        yPos += 8;
        
        autoTable(doc, {
          startY: yPos,
          head: [["Driver", "Completed", "Avg Time (min)"]],
          body: driverPerformance.slice(0, 10).map(d => [
            d.driver_name || "Unknown",
            String(d.completed_deliveries || 0),
            String(d.avg_delivery_time_minutes || 0)
          ]),
          theme: 'striped',
          headStyles: { fillColor: [249, 115, 22] }
        });
        yPos = doc.lastAutoTable ? doc.lastAutoTable.finalY + 12 : yPos + 50;
      }

      // Site Stats Section (on new page if needed)
      if (siteStats && siteStats.length > 0) {
        if (yPos > 200) {
          doc.addPage();
          yPos = 20;
        }
        doc.setFontSize(14);
        doc.text("Top Sites", 14, yPos);
        yPos += 8;
        
        autoTable(doc, {
          startY: yPos,
          head: [["Site", "Total Deliveries"]],
          body: siteStats.slice(0, 10).map(s => [
            s.site_name || "Unknown",
            String(s.total_deliveries || 0)
          ]),
          theme: 'striped',
          headStyles: { fillColor: [59, 130, 246] }
        });
        yPos = doc.lastAutoTable ? doc.lastAutoTable.finalY + 12 : yPos + 50;
      }

      // Customer Stats Section
      if (customerStats && customerStats.length > 0) {
        if (yPos > 200) {
          doc.addPage();
          yPos = 20;
        }
        doc.setFontSize(14);
        doc.text("Customer Performance", 14, yPos);
        yPos += 8;
        
        autoTable(doc, {
          startY: yPos,
          head: [["Customer", "Total", "Completed"]],
          body: customerStats.map(c => [
            c.customer_name || "Unknown",
            String(c.total_deliveries || 0),
            String(c.completed_deliveries || 0)
          ]),
          theme: 'striped',
          headStyles: { fillColor: [34, 197, 94] }
        });
        yPos = doc.lastAutoTable ? doc.lastAutoTable.finalY + 12 : yPos + 50;
      }

      // Site Financials removed - now in dedicated Financials tab

      const fileName = analyticsStartDate && analyticsEndDate 
        ? `analytics_report_${analyticsStartDate}_to_${analyticsEndDate}.pdf`
        : `analytics_report_${new Date().toISOString().split('T')[0]}.pdf`;
      
      doc.save(fileName);
      toast.success("Analytics exported to PDF!");
    } catch (error) {
      console.error("PDF export error:", error);
      toast.error("Failed to export PDF: " + error.message);
    }
  };

  const applyAnalyticsDateFilter = () => {
    fetchAnalytics(analyticsStartDate, analyticsEndDate, analyticsFilterDrivers, analyticsFilterSites, analyticsFilterCustomers);
  };

  const clearAnalyticsDateFilter = () => {
    setAnalyticsStartDate("");
    setAnalyticsEndDate("");
    setAnalyticsFilterDrivers([]);
    setAnalyticsFilterSites([]);
    setAnalyticsFilterCustomers([]);
    fetchAnalytics("", "", [], [], []);
  };

  // Toggle filter selection
  const toggleDriverFilter = (driverId) => {
    setAnalyticsFilterDrivers(prev => 
      prev.includes(driverId) ? prev.filter(id => id !== driverId) : [...prev, driverId]
    );
  };

  const toggleSiteFilter = (siteId) => {
    setAnalyticsFilterSites(prev => 
      prev.includes(siteId) ? prev.filter(id => id !== siteId) : [...prev, siteId]
    );
  };

  const toggleCustomerFilter = (customer) => {
    setAnalyticsFilterCustomers(prev => 
      prev.includes(customer) ? prev.filter(c => c !== customer) : [...prev, customer]
    );
  };

  // Get unique customers from sites
  const uniqueCustomers = [...new Set(sites.map(s => s.customer_name).filter(Boolean))].sort();

  // Sort site financials
  const sortedSiteFinancials = [...siteFinancials].sort((a, b) => {
    let aVal = a[financialsSortColumn];
    let bVal = b[financialsSortColumn];
    
    // Handle string columns
    if (typeof aVal === 'string') {
      aVal = aVal.toLowerCase();
      bVal = bVal.toLowerCase();
    }
    
    if (financialsSortDirection === 'asc') {
      return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
    } else {
      return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
    }
  });

  const handleFinancialsSort = (column) => {
    if (financialsSortColumn === column) {
      setFinancialsSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setFinancialsSortColumn(column);
      setFinancialsSortDirection('desc');
    }
  };

  const SortIcon = ({ column }) => {
    if (financialsSortColumn !== column) return <span className="ml-1 text-white/30">↕</span>;
    return <span className="ml-1">{financialsSortDirection === 'asc' ? '↑' : '↓'}</span>;
  };

  const importDriversFromExcel = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setImportingDrivers(true);
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(sheet);
      
      if (jsonData.length === 0) {
        toast.error("No data found in the file");
        return;
      }
      
      let successCount = 0;
      let errorCount = 0;
      const errors = [];
      
      for (const row of jsonData) {
        // Support multiple column name formats
        const fullName = row["Full Name"] || row["full_name"] || row["Name"] || row["name"];
        const username = row["Username"] || row["username"] || row["User"];
        const truckNumber = row["Truck Number"] || row["truck_number"] || row["Truck"] || row["truck"];
        const password = row["Password"] || row["password"] || "driver123"; // Default password
        
        if (!fullName || !username) {
          errorCount++;
          errors.push(`Row missing Full Name or Username`);
          continue;
        }
        
        try {
          await axios.post(`${API}/admin/drivers`, {
            full_name: fullName,
            username: username.toLowerCase().replace(/\s+/g, ''),
            password: password,
            truck_number: truckNumber || "",
            role: "driver"
          });
          successCount++;
        } catch (err) {
          errorCount++;
          errors.push(`${fullName}: ${err.response?.data?.detail || err.message}`);
        }
      }
      
      if (successCount > 0) {
        toast.success(`Successfully imported ${successCount} driver(s)`);
        fetchData();
      }
      if (errorCount > 0) {
        toast.error(`Failed to import ${errorCount} driver(s). ${errors[0]}`);
        console.log("Import errors:", errors);
      }
    } catch (error) {
      toast.error("Failed to read file: " + error.message);
    } finally {
      setImportingDrivers(false);
      e.target.value = ""; // Reset file input
    }
  };

  const importSitesFromExcel = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setImportingSites(true);
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(sheet);
      
      if (jsonData.length === 0) {
        toast.error("No data found in the file");
        return;
      }
      
      let successCount = 0;
      let errorCount = 0;
      const errors = [];
      
      for (const row of jsonData) {
        // Support multiple column name formats
        const customerName = row["Customer Name"] || row["customer_name"] || row["Customer"] || row["customer"] || "";
        const siteName = row["Site Name"] || row["site_name"] || row["Site"] || row["Name"] || row["name"];
        const address = row["Address"] || row["address"] || "";
        
        if (!siteName) {
          errorCount++;
          errors.push(`Row missing Site Name`);
          continue;
        }
        
        try {
          await axios.post(`${API}/admin/sites`, {
            name: siteName,
            address: address,
            customer_name: customerName
          });
          successCount++;
        } catch (err) {
          errorCount++;
          errors.push(`${siteName}: ${err.response?.data?.detail || err.message}`);
        }
      }
      
      if (successCount > 0) {
        toast.success(`Successfully imported ${successCount} site(s)`);
        fetchData();
      }
      if (errorCount > 0) {
        toast.error(`Failed to import ${errorCount} site(s). ${errors[0]}`);
        console.log("Import errors:", errors);
      }
    } catch (error) {
      toast.error("Failed to read file: " + error.message);
    } finally {
      setImportingSites(false);
      e.target.value = ""; // Reset file input
    }
  };

  const importDeliveriesFromExcel = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setImportingDeliveries(true);
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(sheet);
      
      if (jsonData.length === 0) {
        toast.error("No data found in the file");
        return;
      }
      
      let successCount = 0;
      let errorCount = 0;
      const errors = [];
      
      for (const row of jsonData) {
        // Support multiple column name formats
        const driverName = row["Driver Name"] || row["driver_name"] || row["Driver"] || row["driver"] || "";
        const siteName = row["Site Name"] || row["site_name"] || row["Site"] || row["site"] || "";
        const customerName = row["Customer Name"] || row["customer_name"] || row["Customer"] || "";
        const truckNumber = row["Truck Number"] || row["truck_number"] || row["Truck"] || "";
        const checkinTime = row["Check In Time"] || row["checkin_time"] || row["Check In"] || row["checkin"] || "";
        const checkoutTime = row["Check Out Time"] || row["checkout_time"] || row["Check Out"] || row["checkout"] || "";
        const notes = row["Notes"] || row["notes"] || row["Delivery Notes"] || "";
        const status = row["Status"] || row["status"] || "completed";
        
        if (!driverName || !siteName) {
          errorCount++;
          errors.push(`Row missing Driver Name or Site Name`);
          continue;
        }
        
        // Parse date - try various formats
        let checkinISO = "";
        if (checkinTime) {
          try {
            // If it's already a Date object from Excel
            if (checkinTime instanceof Date) {
              checkinISO = checkinTime.toISOString();
            } else if (typeof checkinTime === 'number') {
              // Excel date serial number
              const date = new Date((checkinTime - 25569) * 86400 * 1000);
              checkinISO = date.toISOString();
            } else {
              // Try parsing string
              const parsed = new Date(checkinTime);
              if (!isNaN(parsed)) {
                checkinISO = parsed.toISOString();
              }
            }
          } catch (e) {
            checkinISO = new Date().toISOString();
          }
        } else {
          checkinISO = new Date().toISOString();
        }
        
        let checkoutISO = "";
        if (checkoutTime) {
          try {
            if (checkoutTime instanceof Date) {
              checkoutISO = checkoutTime.toISOString();
            } else if (typeof checkoutTime === 'number') {
              const date = new Date((checkoutTime - 25569) * 86400 * 1000);
              checkoutISO = date.toISOString();
            } else {
              const parsed = new Date(checkoutTime);
              if (!isNaN(parsed)) {
                checkoutISO = parsed.toISOString();
              }
            }
          } catch (e) {
            checkoutISO = "";
          }
        }
        
        try {
          await axios.post(`${API}/admin/deliveries/import`, {
            driver_name: driverName,
            site_name: siteName,
            customer_name: customerName,
            truck_number: truckNumber,
            checkin_time: checkinISO,
            checkout_time: checkoutISO || null,
            notes: notes,
            status: status
          });
          successCount++;
        } catch (err) {
          errorCount++;
          errors.push(`${driverName}/${siteName}: ${err.response?.data?.detail || err.message}`);
        }
      }
      
      if (successCount > 0) {
        toast.success(`Successfully imported ${successCount} delivery(ies)`);
        fetchData();
      }
      if (errorCount > 0) {
        toast.error(`Failed to import ${errorCount} delivery(ies). ${errors[0]}`);
        console.log("Import errors:", errors);
      }
    } catch (error) {
      toast.error("Failed to read file: " + error.message);
    } finally {
      setImportingDeliveries(false);
      e.target.value = ""; // Reset file input
    }
  };

  const downloadDeliveryTemplate = () => {
    const templateData = [
      { 
        "Driver Name": "John Tan", 
        "Site Name": "Marina Bay Site", 
        "Customer Name": "HDB",
        "Truck Number": "SGX-1234A",
        "Check In Time": "2025-01-27 08:00:00",
        "Check Out Time": "2025-01-27 09:30:00",
        "Notes": "Delivered 5 loads",
        "Status": "completed"
      },
      { 
        "Driver Name": "Mary Lim", 
        "Site Name": "Jurong West Site", 
        "Customer Name": "JTC",
        "Truck Number": "SGX-5678B",
        "Check In Time": "2025-01-27 10:00:00",
        "Check Out Time": "2025-01-27 11:15:00",
        "Notes": "",
        "Status": "completed"
      },
    ];
    
    const ws = XLSX.utils.json_to_sheet(templateData);
    // Set column widths
    ws['!cols'] = [{ wch: 15 }, { wch: 20 }, { wch: 15 }, { wch: 15 }, { wch: 20 }, { wch: 20 }, { wch: 25 }, { wch: 12 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Deliveries Template");
    XLSX.writeFile(wb, "deliveries_import_template.xlsx");
    toast.success("Template downloaded! Fill in your data and import.");
  };

  const downloadDriverTemplate = () => {
    const templateData = [
      { "Full Name": "John Tan", "Username": "johntan", "Truck Number": "SGX-1234A", "Password": "driver123" },
      { "Full Name": "Mary Lim", "Username": "marylim", "Truck Number": "SGX-5678B", "Password": "driver123" },
    ];
    
    const ws = XLSX.utils.json_to_sheet(templateData);
    // Set column widths
    ws['!cols'] = [{ wch: 20 }, { wch: 15 }, { wch: 15 }, { wch: 15 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Drivers Template");
    XLSX.writeFile(wb, "drivers_import_template.xlsx");
    toast.success("Template downloaded! Fill in your data and import.");
  };

  const downloadSiteTemplate = () => {
    const templateData = [
      { "Customer Name": "HDB", "Site Name": "Punggol Housing", "Address": "Punggol Way, Singapore 828654" },
      { "Customer Name": "JTC Corporation", "Site Name": "Jurong West Site", "Address": "Jurong West Ave 1, Singapore 640501" },
    ];
    
    const ws = XLSX.utils.json_to_sheet(templateData);
    // Set column widths
    ws['!cols'] = [{ wch: 20 }, { wch: 25 }, { wch: 40 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sites Template");
    XLSX.writeFile(wb, "sites_import_template.xlsx");
    toast.success("Template downloaded! Fill in your data and import.");
  };

  const clearFilters = () => {
    setSearchTerm("");
    setFilterStatus("all");
    setFilterDriver("all");
    setFilterSite("all");
    setDateRange({ start: "", end: "" });
  };

  // Images gallery functions
  const fetchImages = async () => {
    setImagesLoading(true);
    try {
      // Get all deliveries with photos
      const response = await axios.get(`${API}/deliveries?page=1&limit=1000`);
      const deliveriesWithPhotos = (response.data.data || response.data)
        .filter(d => d.photo || d.has_photo);
      
      // Fetch actual photo data for each delivery (in batches for performance)
      const imagesWithData = [];
      const batchSize = 10;
      
      for (let i = 0; i < deliveriesWithPhotos.length; i += batchSize) {
        const batch = deliveriesWithPhotos.slice(i, i + batchSize);
        const photoPromises = batch.map(async (d) => {
          try {
            const photoRes = await axios.get(`${API}/deliveries/${d.id}/photo`);
            return {
              id: d.id,
              photo: photoRes.data.photo,
              driver_name: d.driver_name,
              site_name: d.site_name,
              customer_name: d.customer_name,
              checkin_time: d.checkin_time,
              checkout_time: d.checkout_time
            };
          } catch (e) {
            console.error(`Failed to fetch photo for ${d.id}:`, e);
            return null;
          }
        });
        
        const results = await Promise.all(photoPromises);
        imagesWithData.push(...results.filter(r => r && r.photo));
      }
      
      setAllImages(imagesWithData);
    } catch (error) {
      console.error("Error fetching images:", error);
      toast.error("Failed to load images");
    } finally {
      setImagesLoading(false);
    }
  };

  const toggleImageSelection = (imageId) => {
    setSelectedImages(prev => 
      prev.includes(imageId) 
        ? prev.filter(id => id !== imageId)
        : [...prev, imageId]
    );
  };

  const selectAllImages = () => {
    const currentPageImages = paginatedImages.map(img => img.id);
    const allSelected = currentPageImages.every(id => selectedImages.includes(id));
    if (allSelected) {
      setSelectedImages(prev => prev.filter(id => !currentPageImages.includes(id)));
    } else {
      setSelectedImages(prev => [...new Set([...prev, ...currentPageImages])]);
    }
  };

  // Compress a base64 image to target size
  const compressImageForDownload = (base64Image, maxSizeKB = 200) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;
        
        // Calculate scale based on current size estimate
        const currentSizeKB = (base64Image.length * 0.75) / 1024;
        let scale = 1;
        if (currentSizeKB > maxSizeKB) {
          scale = Math.sqrt(maxSizeKB / currentSizeKB);
        }
        
        // Max dimensions for reasonable file size
        const maxDimension = 1200;
        if (width > maxDimension || height > maxDimension) {
          if (width > height) {
            scale = Math.min(scale, maxDimension / width);
          } else {
            scale = Math.min(scale, maxDimension / height);
          }
        }
        
        width = Math.round(width * scale);
        height = Math.round(height * scale);
        
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        // Start with quality 0.7 and reduce if needed
        let quality = 0.7;
        let result = canvas.toDataURL('image/jpeg', quality);
        
        // Iteratively reduce quality until under maxSizeKB
        while ((result.length * 0.75) / 1024 > maxSizeKB && quality > 0.1) {
          quality -= 0.1;
          result = canvas.toDataURL('image/jpeg', quality);
        }
        
        resolve(result);
      };
      img.onerror = reject;
      img.src = base64Image;
    });
  };

  // Convert base64 to blob for ZIP
  const base64ToBlob = (base64) => {
    const parts = base64.split(';base64,');
    const contentType = parts[0].split(':')[1];
    const raw = window.atob(parts[1]);
    const rawLength = raw.length;
    const uInt8Array = new Uint8Array(rawLength);
    for (let i = 0; i < rawLength; ++i) {
      uInt8Array[i] = raw.charCodeAt(i);
    }
    return new Blob([uInt8Array], { type: contentType });
  };

  const downloadSelectedImages = async () => {
    const imagesToDownload = allImages.filter(img => selectedImages.includes(img.id));
    if (imagesToDownload.length === 0) {
      toast.error("No images selected");
      return;
    }

    // Single image - download directly without ZIP
    if (imagesToDownload.length === 1) {
      const img = imagesToDownload[0];
      try {
        toast.info("Compressing image...");
        const compressedImage = await compressImageForDownload(img.photo, 200);
        const link = document.createElement('a');
        link.href = compressedImage;
        const timestamp = new Date(img.checkin_time).toISOString().split('T')[0];
        const siteName = (img.site_name || 'unknown').replace(/[^a-zA-Z0-9]/g, '_');
        link.download = `${siteName}_${timestamp}_${img.id.slice(-6)}.jpg`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success("Image downloaded");
      } catch (e) {
        console.error("Download error:", e);
        toast.error("Failed to download image");
      }
      return;
    }

    // Multiple images - create ZIP file
    toast.info(`Compressing ${imagesToDownload.length} images and creating ZIP...`);
    
    const zip = new JSZip();
    let processedCount = 0;
    
    for (const img of imagesToDownload) {
      try {
        // Compress the image
        const compressedImage = await compressImageForDownload(img.photo, 200);
        
        // Generate filename
        const timestamp = new Date(img.checkin_time).toISOString().split('T')[0];
        const siteName = (img.site_name || 'unknown').replace(/[^a-zA-Z0-9]/g, '_');
        const driverName = (img.driver_name || 'unknown').replace(/[^a-zA-Z0-9]/g, '_');
        const filename = `${siteName}_${driverName}_${timestamp}_${img.id.slice(-6)}.jpg`;
        
        // Convert base64 to blob and add to ZIP
        const blob = base64ToBlob(compressedImage);
        zip.file(filename, blob);
        processedCount++;
        
        // Update progress every 5 images
        if (processedCount % 5 === 0) {
          toast.info(`Processed ${processedCount}/${imagesToDownload.length} images...`);
        }
      } catch (e) {
        console.error("Error processing image:", e);
      }
    }
    
    if (processedCount === 0) {
      toast.error("No images could be processed");
      return;
    }
    
    // Generate ZIP file
    toast.info("Creating ZIP file...");
    try {
      const zipBlob = await zip.generateAsync({ 
        type: 'blob',
        compression: 'DEFLATE',
        compressionOptions: { level: 6 }
      });
      
      // Download the ZIP file
      const downloadDate = new Date().toISOString().split('T')[0];
      const zipFilename = `delivery_photos_${downloadDate}_${processedCount}images.zip`;
      
      const link = document.createElement('a');
      link.href = URL.createObjectURL(zipBlob);
      link.download = zipFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
      
      toast.success(`Downloaded ZIP with ${processedCount} compressed images`);
    } catch (e) {
      console.error("ZIP creation error:", e);
      toast.error("Failed to create ZIP file");
    }
  };

  const deleteSelectedImages = async () => {
    if (selectedImages.length === 0) {
      toast.error("No images selected");
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedImages.length} image(s)? This will remove the photos from the deliveries but keep the delivery records.`)) {
      return;
    }

    try {
      let successCount = 0;
      for (const deliveryId of selectedImages) {
        try {
          await axios.delete(`${API}/deliveries/${deliveryId}/photo`);
          successCount++;
        } catch (e) {
          console.error(`Failed to delete photo for delivery ${deliveryId}:`, e);
        }
      }
      
      toast.success(`Deleted ${successCount} photo(s)`);
      setSelectedImages([]);
      fetchImages();
      fetchData();
    } catch (error) {
      toast.error("Failed to delete photos");
    }
  };

  const deleteAllImages = async () => {
    if (allImages.length === 0) {
      toast.error("No images to delete");
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ALL ${allImages.length} image(s)? This action cannot be undone.`)) {
      return;
    }

    // Double confirmation for safety
    if (!window.confirm(`This will permanently remove ${allImages.length} photos. Are you absolutely sure?`)) {
      return;
    }

    try {
      toast.info(`Deleting ${allImages.length} image(s)...`);
      let successCount = 0;
      
      for (const img of allImages) {
        try {
          await axios.delete(`${API}/deliveries/${img.id}/photo`);
          successCount++;
        } catch (e) {
          console.error(`Failed to delete photo for delivery ${img.id}:`, e);
        }
      }
      
      toast.success(`Deleted ${successCount} photo(s)`);
      setSelectedImages([]);
      setAllImages([]);
      fetchData();
    } catch (error) {
      toast.error("Failed to delete photos");
    }
  };

  const paginatedImages = allImages.slice(
    (imagesPage - 1) * IMAGES_PER_PAGE,
    imagesPage * IMAGES_PER_PAGE
  );

  const totalImagePages = Math.ceil(allImages.length / IMAGES_PER_PAGE);

  const downloadPhoto = async (delivery) => {
    try {
      let photoData = delivery.photo;
      
      // If photo is boolean (not loaded), fetch it first
      if (photoData === true || !photoData || typeof photoData !== 'string' || !photoData.startsWith('data:')) {
        toast.info("Fetching photo...");
        const response = await axios.get(`${API}/deliveries/${delivery.id}/photo`);
        photoData = response.data.photo;
      }
      
      if (!photoData || typeof photoData !== 'string') {
        toast.error("Photo is not available");
        return;
      }
      
      // Create a link element
      const link = document.createElement('a');
      link.href = photoData;
      
      // Generate filename with site name and timestamp
      const timestamp = new Date(delivery.checkin_time).toISOString().split('T')[0];
      const siteName = (delivery.site_name || 'unknown').replace(/[^a-zA-Z0-9]/g, '_');
      link.download = `delivery_photo_${siteName}_${timestamp}.jpg`;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success("Photo downloaded successfully!");
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Failed to download photo");
    }
  };

  return (
    <div className="min-h-screen bg-[#0F1115] dark">
      <header className="bg-[#161920] border-b border-white/10 sticky top-0 z-50">
        <div className="px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-white rounded overflow-hidden flex items-center justify-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_bcc851c7-6465-41a4-a49f-44dbf65b5fb7/artifacts/72dfskcd_unity-contractors-logo.jpg" 
                alt="Unity Contractors Logo" 
                className="w-full h-full object-contain"
              />
            </div>
            <div>
              <h1 className="font-heading font-bold text-xl uppercase leading-none text-white" data-testid="admin-dashboard-title">
                UNITY DELIVERY ADMIN
              </h1>
              <p className="text-xs font-mono text-white/60">{user.full_name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { fetchData(); toast.success("Data synced!"); }}
              className="px-3 py-2 text-xs font-heading font-bold uppercase bg-accent-success/20 text-accent-success hover:bg-accent-success/30 border border-accent-success/30 transition-colors flex items-center gap-2"
              data-testid="sync-data-button"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              SYNC
            </button>
            <button
              onClick={onLogout}
              className="p-2 text-white/60 hover:text-white hover:bg-white/5"
              data-testid="admin-logout-button"
            >
              <LogOut className="w-5 h-5" strokeWidth={2} />
            </button>
          </div>
        </div>
      </header>

      <main className="p-6">
        {dataLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-white/60 font-body">Loading data...</p>
            </div>
          </div>
        ) : (
        <>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-[#161920] border border-white/10 p-6" data-testid="total-sites-card">
            <div className="flex items-center justify-between mb-2">
              <Building2 className="w-8 h-8 text-primary" strokeWidth={2} />
              <span className="font-mono text-3xl font-bold text-white">{stats.total_sites}</span>
            </div>
            <p className="text-sm font-body text-white/60 uppercase tracking-wider">Total Sites</p>
          </div>

          <div className="bg-[#161920] border border-white/10 p-6" data-testid="completed-today-card">
            <div className="flex items-center justify-between mb-2">
              <CheckCircle className="w-8 h-8 text-accent-info" strokeWidth={2} />
              <span className="font-mono text-3xl font-bold text-accent-info">{stats.completed_today}</span>
            </div>
            <p className="text-sm font-body text-white/60 uppercase tracking-wider">Completed Today</p>
          </div>

          <div className="bg-[#161920] border border-white/10 p-6" data-testid="total-drivers-card">
            <div className="flex items-center justify-between mb-2">
              <Users className="w-8 h-8 text-accent-warning" strokeWidth={2} />
              <span className="font-mono text-3xl font-bold text-accent-warning">{stats.total_drivers}</span>
            </div>
            <p className="text-sm font-body text-white/60 uppercase tracking-wider">Total Drivers</p>
          </div>
        </div>

        <div className="mb-4 flex flex-wrap gap-2">
          {hasTabAccess("deliveries") && (
            <button
              onClick={() => setView("deliveries")}
              className={`px-3 sm:px-6 h-9 sm:h-10 font-heading font-bold text-xs sm:text-sm uppercase border-2 transition-colors ${
                view === "deliveries"
                  ? "bg-primary border-primary text-white"
                  : "bg-transparent border-white/20 text-white/60 hover:border-white/40 hover:text-white"
              }`}
              data-testid="deliveries-tab"
            >
              DELIVERIES
            </button>
          )}
          {hasTabAccess("drivers") && (
            <button
              onClick={() => setView("drivers")}
              className={`px-3 sm:px-6 h-9 sm:h-10 font-heading font-bold text-xs sm:text-sm uppercase border-2 transition-colors ${
                view === "drivers"
                  ? "bg-primary border-primary text-white"
                  : "bg-transparent border-white/20 text-white/60 hover:border-white/40 hover:text-white"
              }`}
              data-testid="drivers-tab"
            >
              DRIVERS
            </button>
          )}
          {hasTabAccess("sites") && (
            <button
              onClick={() => setView("sites")}
              className={`px-3 sm:px-6 h-9 sm:h-10 font-heading font-bold text-xs sm:text-sm uppercase border-2 transition-colors ${
                view === "sites"
                  ? "bg-primary border-primary text-white"
                  : "bg-transparent border-white/20 text-white/60 hover:border-white/40 hover:text-white"
              }`}
              data-testid="sites-tab"
            >
              SITES
            </button>
          )}
          {hasTabAccess("jobs") && (
            <button
              onClick={() => setView("jobs")}
              className={`px-3 sm:px-6 h-9 sm:h-10 font-heading font-bold text-xs sm:text-sm uppercase border-2 transition-colors ${
                view === "jobs"
                  ? "bg-primary border-primary text-white"
                  : "bg-transparent border-white/20 text-white/60 hover:border-white/40 hover:text-white"
              }`}
              data-testid="jobs-tab"
            >
              <Briefcase className="w-4 h-4 inline mr-1 sm:mr-2" strokeWidth={2} />
              JOBS
              {unfinishedJobs.total_unfinished > 0 && (
                <span className="ml-1 px-1.5 py-0.5 text-xs bg-red-500 text-white rounded-full">
                  {unfinishedJobs.total_unfinished}
                </span>
              )}
            </button>
          )}
          {hasTabAccess("account") && (
            <button
              onClick={() => setView("account")}
              className={`px-3 sm:px-6 h-9 sm:h-10 font-heading font-bold text-xs sm:text-sm uppercase border-2 transition-colors ${
                view === "account"
                  ? "bg-primary border-primary text-white"
                  : "bg-transparent border-white/20 text-white/60 hover:border-white/40 hover:text-white"
              }`}
              data-testid="account-tab"
            >
              ACCOUNT
            </button>
          )}
          {/* Managers tab - visible to admins only */}
          {user?.role === "admin" && (
            <button
              onClick={() => setView("managers")}
              className={`px-3 sm:px-6 h-9 sm:h-10 font-heading font-bold text-xs sm:text-sm uppercase border-2 transition-colors ${
                view === "managers"
                  ? "bg-primary border-primary text-white"
                  : "bg-transparent border-white/20 text-white/60 hover:border-white/40 hover:text-white"
              }`}
              data-testid="managers-tab"
            >
              <UserCog className="w-4 h-4 inline mr-1 sm:mr-2" strokeWidth={2} />
              MANAGERS
            </button>
          )}
          {/* Admins tab - visible to admins only, hidden from managers */}
          {user?.role === "admin" && (
            <button
              onClick={() => setView("admins")}
              className={`px-3 sm:px-6 h-9 sm:h-10 font-heading font-bold text-xs sm:text-sm uppercase border-2 transition-colors ${
                view === "admins"
                  ? "bg-primary border-primary text-white"
                  : "bg-transparent border-white/20 text-white/60 hover:border-white/40 hover:text-white"
              }`}
              data-testid="admins-tab"
            >
              ADMINS
            </button>
          )}
          {hasTabAccess("analytics") && (
            <button
              onClick={() => setView("analytics")}
              className={`px-3 sm:px-6 h-9 sm:h-10 font-heading font-bold text-xs sm:text-sm uppercase border-2 transition-colors ${
                view === "analytics"
                  ? "bg-primary border-primary text-white"
                  : "bg-transparent border-white/20 text-white/60 hover:border-white/40 hover:text-white"
              }`}
              data-testid="analytics-tab"
            >
              <BarChart3 className="w-4 h-4 inline mr-1 sm:mr-2" strokeWidth={2} />
              ANALYTICS
            </button>
          )}
          {hasTabAccess("financials") && (
            <button
              onClick={() => setView("financials")}
              className={`px-3 sm:px-6 h-9 sm:h-10 font-heading font-bold text-xs sm:text-sm uppercase border-2 transition-colors ${
                view === "financials"
                  ? "bg-primary border-primary text-white"
                  : "bg-transparent border-white/20 text-white/60 hover:border-white/40 hover:text-white"
              }`}
              data-testid="financials-tab"
            >
              <TrendingUp className="w-4 h-4 inline mr-1 sm:mr-2" strokeWidth={2} />
              FINANCIALS
            </button>
          )}
          {hasTabAccess("images") && (
            <button
              onClick={() => { setView("images"); fetchImages(); }}
              className={`px-3 sm:px-6 h-9 sm:h-10 font-heading font-bold text-xs sm:text-sm uppercase border-2 transition-colors ${
                view === "images"
                  ? "bg-primary border-primary text-white"
                  : "bg-transparent border-white/20 text-white/60 hover:border-white/40 hover:text-white"
              }`}
              data-testid="images-tab"
            >
              <ImageIcon className="w-4 h-4 inline mr-1 sm:mr-2" strokeWidth={2} />
              IMAGES
            </button>
          )}
          {hasTabAccess("maintenance") && (
            <button
              onClick={() => setView("maintenance")}
              className={`px-3 sm:px-6 h-9 sm:h-10 font-heading font-bold text-xs sm:text-sm uppercase border-2 transition-colors ${
                view === "maintenance"
                  ? "bg-primary border-primary text-white"
                  : "bg-transparent border-white/20 text-white/60 hover:border-white/40 hover:text-white"
              }`}
              data-testid="maintenance-tab"
            >
              <Wrench className="w-4 h-4 inline mr-1 sm:mr-2" strokeWidth={2} />
              MAINTENANCE
            </button>
          )}
          {hasTabAccess("dumping") && (
            <button
              onClick={() => setView("dumping")}
              className={`px-3 sm:px-6 h-9 sm:h-10 font-heading font-bold text-xs sm:text-sm uppercase border-2 transition-colors ${
                view === "dumping"
                  ? "bg-primary border-primary text-white"
                  : "bg-transparent border-white/20 text-white/60 hover:border-white/40 hover:text-white"
              }`}
              data-testid="dumping-tab"
            >
              <MapPin className="w-4 h-4 inline mr-1 sm:mr-2" strokeWidth={2} />
              DUMPING GROUNDS
            </button>
          )}
          {/* Projects tab */}
          {hasTabAccess("projects") && (
            <button
              onClick={() => setView("projects")}
              className={`px-3 sm:px-6 h-9 sm:h-10 font-heading font-bold text-xs sm:text-sm uppercase border-2 transition-colors ${
                view === "projects"
                  ? "bg-primary border-primary text-white"
                  : "bg-transparent border-white/20 text-white/60 hover:border-white/40 hover:text-white"
              }`}
              data-testid="projects-tab"
            >
              <Calculator className="w-4 h-4 inline mr-1 sm:mr-2" strokeWidth={2} />
              PROJECTS
            </button>
          )}
        </div>

        {view === "deliveries" && (
          <div className="space-y-4">
            {/* Data Retention Notice */}
            <div className="flex items-center gap-2 px-4 py-2 bg-accent-info/10 border border-accent-info/20 text-accent-info">
              <Info className="w-4 h-4 flex-shrink-0" strokeWidth={2} />
              <span className="text-sm font-body">{DATA_RETENTION_TEXT}</span>
            </div>
            
            <div className="bg-[#161920] border border-white/10 p-4">
              <div className="flex flex-wrap items-center gap-4">
                {/* Search */}
                <div className="flex-1 min-w-[300px]">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-white/40" strokeWidth={2} />
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Search by site, driver, or truck..."
                      className="w-full h-10 pl-10 pr-4 bg-transparent border border-white/20 focus:border-primary focus:outline-none text-white text-sm font-body"
                      data-testid="search-input"
                    />
                  </div>
                </div>

                {/* Filter Toggle */}
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`px-4 h-10 flex items-center gap-2 border transition-colors ${
                    showFilters ? "bg-primary border-primary text-white" : "bg-transparent border-white/20 text-white/60 hover:border-white/40"
                  }`}
                  data-testid="filter-toggle"
                >
                  <Filter className="w-4 h-4" strokeWidth={2} />
                  <span className="font-heading font-bold text-xs uppercase">Filters</span>
                  {(filterStatus !== "all" || filterDriver !== "all" || filterSite !== "all" || dateRange.start || dateRange.end) && (
                    <span className="ml-1 px-1.5 py-0.5 bg-primary text-white text-xs rounded-full">{
                      [filterStatus !== "all", filterDriver !== "all", filterSite !== "all", dateRange.start, dateRange.end].filter(Boolean).length
                    }</span>
                  )}
                </button>

                {/* Export Buttons */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={exportToCSV}
                    className="px-4 h-10 flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/20 text-white transition-colors"
                    data-testid="export-csv-button"
                  >
                    <FileText className="w-4 h-4" strokeWidth={2} />
                    <span className="font-heading font-bold text-xs uppercase">CSV</span>
                  </button>
                  <button
                    onClick={exportToExcel}
                    className="px-4 h-10 flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/20 text-white transition-colors"
                    data-testid="export-excel-button"
                  >
                    <FileSpreadsheet className="w-4 h-4" strokeWidth={2} />
                    <span className="font-heading font-bold text-xs uppercase">XLSX</span>
                  </button>
                </div>

                {/* Import Buttons */}
                <div className="flex items-center gap-2">
                  <input
                    type="file"
                    ref={deliveryFileInputRef}
                    onChange={importDeliveriesFromExcel}
                    accept=".xlsx,.xls"
                    className="hidden"
                    data-testid="import-deliveries-input"
                  />
                  <button
                    onClick={() => deliveryFileInputRef.current?.click()}
                    disabled={importingDeliveries}
                    className="px-4 h-10 flex items-center gap-2 bg-accent-success/20 hover:bg-accent-success/30 border border-accent-success/40 text-accent-success transition-colors disabled:opacity-50"
                    data-testid="import-deliveries-button"
                  >
                    <Upload className="w-4 h-4" strokeWidth={2} />
                    <span className="font-heading font-bold text-xs uppercase">
                      {importingDeliveries ? "Importing..." : "Import"}
                    </span>
                  </button>
                  <button
                    onClick={downloadDeliveryTemplate}
                    className="px-4 h-10 flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/20 text-white/60 hover:text-white transition-colors"
                    data-testid="download-delivery-template-button"
                    title="Download import template"
                  >
                    <Download className="w-4 h-4" strokeWidth={2} />
                    <span className="font-heading font-bold text-xs uppercase">Template</span>
                  </button>
                </div>
              </div>

              {/* Advanced Filters */}
              {showFilters && (
                <div className="mt-4 pt-4 border-t border-white/10">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                    <div>
                      <label className="block text-xs font-semibold font-body text-white/60 uppercase mb-2">Status</label>
                      <select
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                        className="w-full h-10 px-3 bg-[#1a1d24] border border-white/20 focus:border-primary focus:outline-none text-white text-sm"
                        data-testid="filter-status"
                      >
                        <option value="all" className="bg-[#1a1d24] text-white">All Status</option>
                        <option value="active" className="bg-[#1a1d24] text-white">Active</option>
                        <option value="completed" className="bg-[#1a1d24] text-white">Completed</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold font-body text-white/60 uppercase mb-2">Driver</label>
                      <select
                        value={filterDriver}
                        onChange={(e) => setFilterDriver(e.target.value)}
                        className="w-full h-10 px-3 bg-[#1a1d24] border border-white/20 focus:border-primary focus:outline-none text-white text-sm"
                        data-testid="filter-driver"
                      >
                        <option value="all" className="bg-[#1a1d24] text-white">All Drivers</option>
                        {drivers.map(driver => (
                          <option key={driver.id} value={driver.id} className="bg-[#1a1d24] text-white">{driver.full_name}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold font-body text-white/60 uppercase mb-2">Site</label>
                      <select
                        value={filterSite}
                        onChange={(e) => setFilterSite(e.target.value)}
                        className="w-full h-10 px-3 bg-[#1a1d24] border border-white/20 focus:border-primary focus:outline-none text-white text-sm"
                        data-testid="filter-site"
                      >
                        <option value="all" className="bg-[#1a1d24] text-white">All Sites</option>
                        {sites.map(site => (
                          <option key={site.id} value={site.id} className="bg-[#1a1d24] text-white">{site.name}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold font-body text-white/60 uppercase mb-2">Start Date</label>
                      <input
                        type="date"
                        value={dateRange.start}
                        onChange={(e) => {
                          setDateRange(prev => ({ ...prev, start: e.target.value }));
                        }}
                        className="w-full h-10 px-3 bg-transparent border border-white/20 focus:border-primary focus:outline-none text-white text-sm"
                        data-testid="filter-start-date"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold font-body text-white/60 uppercase mb-2">End Date</label>
                      <input
                        type="date"
                        value={dateRange.end}
                        onChange={(e) => {
                          setDateRange(prev => ({ ...prev, end: e.target.value }));
                        }}
                        className="w-full h-10 px-3 bg-transparent border border-white/20 focus:border-primary focus:outline-none text-white text-sm"
                        data-testid="filter-end-date"
                      />
                    </div>
                  </div>

                  <div className="mt-4 flex items-center gap-2">
                    <button
                      onClick={clearFilters}
                      className="px-4 h-9 bg-transparent border border-white/20 text-white/60 hover:text-white hover:border-white/40 font-heading font-bold text-xs uppercase transition-colors"
                      data-testid="clear-filters-button"
                    >
                      Clear All Filters
                    </button>
                    <div className="text-xs text-white/60 font-mono">
                      Showing {Math.min((serverPage - 1) * 500 + 1, totalDeliveries)}-{Math.min(serverPage * 500, totalDeliveries)} of {totalDeliveries} deliveries (Page {serverPage}/{serverPages})
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Bulk Actions Bar */}
            {user.role === 'admin' && selectedDeliveries.length > 0 && (
              <div className="bg-red-500/10 border border-red-500/30 p-4 mb-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-red-400 font-bold">{selectedDeliveries.length} selected</span>
                  <button
                    onClick={() => setSelectedDeliveries([])}
                    className="text-sm text-white/60 hover:text-white underline"
                  >
                    Clear selection
                  </button>
                </div>
                <button
                  onClick={handleBulkDeleteDeliveries}
                  className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white font-bold text-sm flex items-center gap-2"
                  data-testid="bulk-delete-deliveries-btn"
                >
                  <Trash2 className="w-4 h-4" strokeWidth={2} />
                  DELETE SELECTED
                </button>
              </div>
            )}

            {/* Records count display - always visible */}
            <div className="flex items-center justify-between mb-3 px-1">
              <div className="text-sm text-white/70 font-mono" data-testid="deliveries-count">
                {deliveriesLoading ? (
                  <span className="text-white/40">Loading...</span>
                ) : (
                  <>
                    <span className="text-primary font-bold">{totalDeliveries.toLocaleString()}</span> deliveries found
                    {(filterDriver !== "all" || filterSite !== "all" || filterStatus !== "all" || searchTerm) && (
                      <span className="text-yellow-400 ml-2">(filtered)</span>
                    )}
                  </>
                )}
              </div>
              <div className="text-xs text-white/50 font-mono">
                Page {serverPage} of {serverPages || 1}
              </div>
            </div>

            <div className="bg-[#161920] border border-white/10 overflow-hidden">
              <div className="overflow-x-auto">
              <table className="w-full" data-testid="deliveries-table">
                <thead>
                  <tr className="border-b border-white/10">
                    {user.role === 'admin' && (
                      <th className="px-4 py-3 text-center w-12">
                        <input
                          type="checkbox"
                          checked={paginatedDeliveries.length > 0 && selectedDeliveries.length === paginatedDeliveries.length}
                          onChange={toggleSelectAllDeliveries}
                          className="w-4 h-4 rounded border-white/20 bg-transparent text-primary focus:ring-primary cursor-pointer"
                          data-testid="select-all-deliveries-checkbox"
                        />
                      </th>
                    )}
                    <th 
                      onClick={() => handleSort('customer_name')}
                      className="px-4 py-3 text-left text-xs font-heading font-bold text-white/60 uppercase tracking-wider cursor-pointer hover:text-white transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        Customer
                        {sortConfig.key === 'customer_name' && (
                          <span className="text-primary">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </div>
                    </th>
                    <th 
                      onClick={() => handleSort('site_name')}
                      className="px-4 py-3 text-left text-xs font-heading font-bold text-white/60 uppercase tracking-wider cursor-pointer hover:text-white transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        Site
                        {sortConfig.key === 'site_name' && (
                          <span className="text-primary">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </div>
                    </th>
                    <th 
                      onClick={() => handleSort('driver_name')}
                      className="px-4 py-3 text-left text-xs font-heading font-bold text-white/60 uppercase tracking-wider cursor-pointer hover:text-white transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        Driver
                        {sortConfig.key === 'driver_name' && (
                          <span className="text-primary">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </div>
                    </th>
                    <th 
                      onClick={() => handleSort('truck_number')}
                      className="px-4 py-3 text-left text-xs font-heading font-bold text-white/60 uppercase tracking-wider cursor-pointer hover:text-white transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        Truck
                        {sortConfig.key === 'truck_number' && (
                          <span className="text-primary">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </div>
                    </th>
                    <th 
                      onClick={() => handleSort('checkin_time')}
                      className="px-4 py-3 text-left text-xs font-heading font-bold text-white/60 uppercase tracking-wider cursor-pointer hover:text-white transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        Check-in
                        {sortConfig.key === 'checkin_time' && (
                          <span className="text-primary">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </div>
                    </th>
                    <th 
                      onClick={() => handleSort('checkout_time')}
                      className="px-4 py-3 text-left text-xs font-heading font-bold text-white/60 uppercase tracking-wider cursor-pointer hover:text-white transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        Check-out
                        {sortConfig.key === 'checkout_time' && (
                          <span className="text-primary">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </div>
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-heading font-bold text-white/60 uppercase tracking-wider">
                      GPS
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-heading font-bold text-white/60 uppercase tracking-wider">
                      Photo
                    </th>
                    <th 
                      onClick={() => handleSort('status')}
                      className="px-4 py-3 text-left text-xs font-heading font-bold text-white/60 uppercase tracking-wider cursor-pointer hover:text-white transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        Status
                        {sortConfig.key === 'status' && (
                          <span className="text-primary">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </div>
                    </th>
                    {user.role === 'admin' && (
                      <th className="px-4 py-3 text-center text-xs font-heading font-bold text-white/60 uppercase tracking-wider">
                        Actions
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {deliveriesLoading ? (
                    <tr>
                      <td colSpan={user.role === 'admin' ? "11" : "9"} className="px-4 py-12 text-center">
                        <div className="flex items-center justify-center gap-3">
                          <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                          <span className="text-white/60 font-body">Loading deliveries...</span>
                        </div>
                      </td>
                    </tr>
                  ) : paginatedDeliveries.length === 0 ? (
                    <tr>
                      <td colSpan={user.role === 'admin' ? "11" : "9"} className="px-4 py-12 text-center text-white/40 font-body">
                        No deliveries found matching your filters
                      </td>
                    </tr>
                  ) : (
                    paginatedDeliveries.map((delivery, index) => (
                    <tr
                      key={delivery.id}
                      className={`border-b border-white/5 hover:bg-white/5 ${
                        index % 2 === 0 ? "bg-black/20" : ""
                      } ${selectedDeliveries.includes(delivery.id) ? "bg-red-500/10" : ""}`}
                      data-testid={`delivery-row-${delivery.id}`}
                    >
                      {user.role === 'admin' && (
                        <td className="px-4 py-3 text-center">
                          <input
                            type="checkbox"
                            checked={selectedDeliveries.includes(delivery.id)}
                            onChange={() => toggleDeliverySelection(delivery.id)}
                            className="w-4 h-4 rounded border-white/20 bg-transparent text-primary focus:ring-primary cursor-pointer"
                            data-testid={`select-delivery-${delivery.id}`}
                          />
                        </td>
                      )}
                      <td className="px-4 py-3 font-body text-sm text-white">
                        {delivery.customer_name || "-"}
                      </td>
                      <td className="px-4 py-3 font-body text-sm text-white">{delivery.site_name}</td>
                      <td className="px-4 py-3 font-body text-sm text-white">{delivery.driver_name}</td>
                      <td className="px-4 py-3 font-mono text-sm text-white">{delivery.truck_number}</td>
                      <td className="px-4 py-3 font-mono text-xs text-white">
                        {formatDateTimeDMY(delivery.checkin_time)}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-white">
                        {delivery.checkout_time ? formatDateTimeDMY(delivery.checkout_time) : "-"}
                      </td>
                      <td className="px-4 py-3">
                        {delivery.checkin_location ? (
                          <span className="text-xs text-accent-success font-mono" title={`${delivery.checkin_location.lat}, ${delivery.checkin_location.lng}`}>
                            ✓ {delivery.checkin_location.lat.toFixed(4)}, {delivery.checkin_location.lng.toFixed(4)}
                          </span>
                        ) : (
                          <span className="text-xs text-white/40">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {(delivery.photo || delivery.has_photo) ? (
                          <button
                            onClick={async () => {
                              // Show loading state immediately
                              setSelectedPhoto({ ...delivery, photo: null, loading: true });
                              // Load photo on demand
                              try {
                                const res = await axios.get(`${API}/deliveries/${delivery.id}/photo`);
                                setSelectedPhoto({ ...delivery, photo: res.data.photo, loading: false });
                              } catch (e) {
                                setSelectedPhoto(null);
                                toast.error("Failed to load photo");
                              }
                            }}
                            className="flex items-center gap-1 text-xs text-accent-info hover:text-accent-info/80 transition-colors"
                            data-testid={`view-photo-${delivery.id}`}
                          >
                            <ImageIcon className="w-4 h-4" strokeWidth={2} />
                            <span className="font-body">View</span>
                          </button>
                        ) : (
                          <span className="text-xs text-white/40">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`px-2 py-1 text-xs font-bold uppercase tracking-widest ${
                            delivery.status === "active"
                              ? "bg-accent-success/10 text-accent-success border border-accent-success/20"
                              : "bg-accent-info/10 text-accent-info border border-accent-info/20"
                          }`}
                        >
                          {delivery.status}
                        </span>
                      </td>
                      {user.role === 'admin' && (
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => handleDeleteDelivery(delivery.id)}
                            className="p-1.5 bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors"
                            title="Delete delivery"
                            data-testid={`delete-delivery-${delivery.id}`}
                          >
                            <Trash2 className="w-4 h-4" strokeWidth={2} />
                          </button>
                        </td>
                      )}
                    </tr>
                  )))}
                </tbody>
              </table>
            </div>
            
            {/* Pagination Controls - always visible */}
            <div className="flex items-center justify-between px-4 py-3 border-t border-white/10">
              <div className="text-sm text-white/60 font-body">
                Page {serverPage} of {serverPages || 1} ({totalDeliveries.toLocaleString()} deliveries)
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setServerPage(1)}
                  disabled={serverPage === 1}
                  className="px-3 py-1 text-sm font-body bg-white/5 text-white disabled:opacity-30 disabled:cursor-not-allowed hover:bg-white/10"
                  data-testid="pagination-first"
                >
                  First
                </button>
                <button
                  onClick={() => setServerPage(p => Math.max(1, p - 1))}
                  disabled={serverPage === 1}
                  className="px-3 py-1 text-sm font-body bg-white/5 text-white disabled:opacity-30 disabled:cursor-not-allowed hover:bg-white/10"
                  data-testid="pagination-prev"
                >
                  Prev
                </button>
                <span className="px-4 py-1 text-sm font-mono text-white bg-primary">{serverPage}</span>
                <button
                  onClick={() => setServerPage(p => Math.min(serverPages, p + 1))}
                  disabled={serverPage >= serverPages}
                  className="px-3 py-1 text-sm font-body bg-white/5 text-white disabled:opacity-30 disabled:cursor-not-allowed hover:bg-white/10"
                  data-testid="pagination-next"
                >
                  Next
                </button>
                <button
                  onClick={() => setServerPage(serverPages)}
                  disabled={serverPage >= serverPages}
                  className="px-3 py-1 text-sm font-body bg-white/5 text-white disabled:opacity-30 disabled:cursor-not-allowed hover:bg-white/10"
                  data-testid="pagination-last"
                >
                  Last
                </button>
              </div>
            </div>
          </div>
        </div>
        )}

        {view === "drivers" && (
          <div className="space-y-4">
            <div className="bg-[#161920] border border-white/10 p-4">
              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={() => setShowCreateDriver(true)}
                  className="px-3 sm:px-6 h-10 sm:h-12 bg-primary text-white font-heading font-bold text-xs sm:text-sm uppercase shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[2px] hover:translate-y-[2px] transition-all whitespace-nowrap"
                  data-testid="create-driver-button"
                >
                  <UserPlus className="w-4 h-4 inline mr-1 sm:mr-2" strokeWidth={2} />
                  <span className="hidden sm:inline">CREATE NEW </span>DRIVER
                </button>
                <button
                  onClick={exportDriversToExcel}
                  className="px-3 h-10 sm:h-12 flex items-center gap-1 sm:gap-2 bg-white/5 hover:bg-white/10 border border-white/20 text-white transition-colors whitespace-nowrap"
                  data-testid="export-drivers-button"
                >
                  <FileSpreadsheet className="w-4 h-4" strokeWidth={2} />
                  <span className="font-heading font-bold text-xs uppercase">EXPORT</span>
                </button>
                <input
                  type="file"
                  ref={driverFileInputRef}
                  onChange={importDriversFromExcel}
                  accept=".xlsx,.xls"
                  className="hidden"
                  data-testid="import-drivers-input"
                />
                <button
                  onClick={() => driverFileInputRef.current?.click()}
                  disabled={importingDrivers}
                  className="px-3 h-10 sm:h-12 flex items-center gap-1 sm:gap-2 bg-accent-success/10 hover:bg-accent-success/20 border border-accent-success/30 text-accent-success transition-colors disabled:opacity-50 whitespace-nowrap"
                  data-testid="import-drivers-button"
                >
                  <Upload className="w-4 h-4" strokeWidth={2} />
                  <span className="font-heading font-bold text-xs uppercase">
                    {importingDrivers ? "..." : "IMPORT"}
                  </span>
                </button>
                <button
                  onClick={downloadDriverTemplate}
                  className="px-3 h-10 sm:h-12 flex items-center gap-1 sm:gap-2 bg-white/5 hover:bg-white/10 border border-white/20 text-white/70 hover:text-white transition-colors whitespace-nowrap"
                  data-testid="download-driver-template"
                >
                  <Download className="w-4 h-4" strokeWidth={2} />
                  <span className="font-heading font-bold text-xs uppercase">TEMPLATE</span>
                </button>
              </div>
            </div>

            {showCreateDriver && (
              <div className="bg-[#161920] border border-white/10 p-6">
                <h3 className="font-heading font-bold text-lg uppercase text-white mb-4">NEW DRIVER</h3>
                <form onSubmit={handleCreateDriver} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold font-body text-white/80 mb-2">USERNAME</label>
                      <input
                        type="text"
                        value={newDriver.username}
                        onChange={(e) => setNewDriver({ ...newDriver, username: e.target.value })}
                        required
                        className="w-full h-12 px-4 bg-transparent border-2 border-white/10 focus:border-primary focus:outline-none font-mono text-sm text-white"
                        data-testid="new-driver-username"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold font-body text-white/80 mb-2">PASSWORD</label>
                      <input
                        type="password"
                        value={newDriver.password}
                        onChange={(e) => setNewDriver({ ...newDriver, password: e.target.value })}
                        required
                        className="w-full h-12 px-4 bg-transparent border-2 border-white/10 focus:border-primary focus:outline-none font-mono text-sm text-white"
                        data-testid="new-driver-password"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold font-body text-white/80 mb-2">FULL NAME</label>
                      <input
                        type="text"
                        value={newDriver.full_name}
                        onChange={(e) => setNewDriver({ ...newDriver, full_name: e.target.value })}
                        required
                        className="w-full h-12 px-4 bg-transparent border-2 border-white/10 focus:border-primary focus:outline-none font-body text-sm text-white"
                        data-testid="new-driver-fullname"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold font-body text-white/80 mb-2">TRUCK NUMBER</label>
                      <input
                        type="text"
                        value={newDriver.truck_number}
                        onChange={(e) => setNewDriver({ ...newDriver, truck_number: e.target.value })}
                        className="w-full h-12 px-4 bg-transparent border-2 border-white/10 focus:border-primary focus:outline-none font-mono text-sm text-white"
                        data-testid="new-driver-truck"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold font-body text-white/80 mb-2">WHATSAPP NUMBER</label>
                      <input
                        type="tel"
                        value={newDriver.phone}
                        onChange={(e) => setNewDriver({ ...newDriver, phone: e.target.value })}
                        placeholder="+65 9123 4567"
                        className="w-full h-12 px-4 bg-transparent border-2 border-white/10 focus:border-primary focus:outline-none font-mono text-sm text-white"
                        data-testid="new-driver-phone"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="submit"
                      className="px-6 h-12 bg-primary text-white font-heading font-bold text-sm uppercase"
                      data-testid="submit-driver-button"
                    >
                      CREATE DRIVER
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowCreateDriver(false)}
                      className="px-6 h-12 bg-transparent border-2 border-white/20 text-white font-heading font-bold text-sm uppercase hover:border-white/40"
                      data-testid="cancel-driver-button"
                    >
                      CANCEL
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Search Bar for Drivers */}
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-white/40" strokeWidth={2} />
              <input
                type="text"
                value={driverSearchTerm}
                onChange={(e) => setDriverSearchTerm(e.target.value)}
                placeholder="Search drivers by name, username or truck number..."
                className="w-full h-12 pl-12 pr-4 bg-[#161920] border border-white/10 focus:border-primary focus:outline-none font-body text-sm text-white placeholder-white/40"
                data-testid="driver-search-input"
              />
            </div>

            <div className="grid grid-cols-1 gap-4">
              {filteredDrivers.map((driver) => (
                <DriverCard
                  key={driver.id}
                  driver={driver}
                  sites={sites}
                  isEditing={editingDriver?.id === driver.id}
                  isAssigning={selectedDriver?.id === driver.id}
                  onEdit={(data) => setEditingDriver(data)}
                  onSave={handleUpdateDriver}
                  onCancelEdit={() => setEditingDriver(null)}
                  onAssign={(driverId) => setSelectedDriver(selectedDriver?.id === driverId ? null : driver)}
                  onSaveAssignment={handleAssignSites}
                  onDelete={handleDeleteDriver}
                />
              ))}
            </div>
          </div>
        )}

        {view === "sites" && (
          <div className="space-y-4">
            <div className="bg-[#161920] border border-white/10 p-4">
              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={() => setShowCreateSite(true)}
                  className="px-3 sm:px-6 h-10 sm:h-12 bg-primary text-white font-heading font-bold text-xs sm:text-sm uppercase shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[2px] hover:translate-y-[2px] transition-all whitespace-nowrap"
                  data-testid="create-site-button"
                >
                  <Plus className="w-4 h-4 inline mr-1 sm:mr-2" strokeWidth={2} />
                  <span className="hidden sm:inline">CREATE NEW </span>SITE
                </button>
                <button
                  onClick={exportSitesToExcel}
                  className="px-3 h-10 sm:h-12 flex items-center gap-1 sm:gap-2 bg-white/5 hover:bg-white/10 border border-white/20 text-white transition-colors whitespace-nowrap"
                  data-testid="export-sites-button"
                >
                  <FileSpreadsheet className="w-4 h-4" strokeWidth={2} />
                  <span className="font-heading font-bold text-xs uppercase">EXPORT</span>
                </button>
                <input
                  type="file"
                  ref={siteFileInputRef}
                  onChange={importSitesFromExcel}
                  accept=".xlsx,.xls"
                  className="hidden"
                  data-testid="import-sites-input"
                />
                <button
                  onClick={() => siteFileInputRef.current?.click()}
                  disabled={importingSites}
                  className="px-3 h-10 sm:h-12 flex items-center gap-1 sm:gap-2 bg-accent-success/10 hover:bg-accent-success/20 border border-accent-success/30 text-accent-success transition-colors disabled:opacity-50 whitespace-nowrap"
                  data-testid="import-sites-button"
                >
                  <Upload className="w-4 h-4" strokeWidth={2} />
                  <span className="font-heading font-bold text-xs uppercase">
                    {importingSites ? "..." : "IMPORT"}
                  </span>
                </button>
                <button
                  onClick={downloadSiteTemplate}
                  className="px-3 h-10 sm:h-12 flex items-center gap-1 sm:gap-2 bg-white/5 hover:bg-white/10 border border-white/20 text-white/70 hover:text-white transition-colors whitespace-nowrap"
                  data-testid="download-site-template"
                >
                  <Download className="w-4 h-4" strokeWidth={2} />
                  <span className="font-heading font-bold text-xs uppercase">TEMPLATE</span>
                </button>
              </div>
            </div>

            {showCreateSite && (
              <div className="bg-[#161920] border border-white/10 p-6">
                <h3 className="font-heading font-bold text-lg uppercase text-white mb-4">NEW SITE</h3>
                <form onSubmit={handleCreateSite} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold font-body text-white/80 mb-2">CUSTOMER NAME</label>
                      <input
                        type="text"
                        value={newSite.customer_name}
                        onChange={(e) => setNewSite({ ...newSite, customer_name: e.target.value })}
                        className="w-full h-12 px-4 bg-transparent border-2 border-white/10 focus:border-primary focus:outline-none font-body text-sm text-white"
                        data-testid="new-site-customer"
                      />
                      {(newSite.customer_name?.includes("(") || newSite.customer_name?.includes(")") || newSite.customer_name?.includes("-")) && (
                        <p className="text-xs text-yellow-400 mt-1">Note: ( ), - will be replaced with spaces on save.</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-semibold font-body text-white/80 mb-2 flex items-center gap-1">
                        SITE NAME
                        <span
                          className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full bg-white/10 text-white/60 text-[10px] cursor-help"
                          title="Any ( ) or - in the name will be replaced with a space when saved (WhatsApp Channel Policy)."
                        >
                          ?
                        </span>
                      </label>
                      <input
                        type="text"
                        value={newSite.name}
                        onChange={(e) => setNewSite({ ...newSite, name: e.target.value })}
                        required
                        className="w-full h-12 px-4 bg-transparent border-2 border-white/10 focus:border-primary focus:outline-none font-body text-sm text-white"
                        data-testid="new-site-name"
                      />
                      {(newSite.name.includes("(") || newSite.name.includes(")") || newSite.name.includes("-")) && (
                        <p className="text-xs text-yellow-400 mt-1">Note: ( ), - will be replaced with spaces on save.</p>
                      )}
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm font-semibold font-body text-white/80 mb-2">ADDRESS</label>
                      <input
                        type="text"
                        value={newSite.address}
                        onChange={(e) => setNewSite({ ...newSite, address: e.target.value })}
                        required
                        className="w-full h-12 px-4 bg-transparent border-2 border-white/10 focus:border-primary focus:outline-none font-body text-sm text-white"
                        data-testid="new-site-address"
                      />
                    </div>
                    {/* Cost/Sales fields - Admin and Manager */}
                    {(user?.role === "admin" || user?.role === "manager") && (
                      <>
                        <div>
                          <label className="block text-sm font-semibold font-body text-white/80 mb-2">COST PER LOAD ($)</label>
                          <input
                            type="number"
                            step="0.01"
                            value={newSite.cost}
                            onChange={(e) => setNewSite({ ...newSite, cost: e.target.value })}
                            className="w-full h-12 px-4 bg-transparent border-2 border-white/10 focus:border-primary focus:outline-none font-body text-sm text-white"
                            placeholder="0.00"
                            data-testid="new-site-cost"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold font-body text-white/80 mb-2">SALES PER LOAD ($)</label>
                          <input
                            type="number"
                            step="0.01"
                            value={newSite.sales}
                            onChange={(e) => setNewSite({ ...newSite, sales: e.target.value })}
                            className="w-full h-12 px-4 bg-transparent border-2 border-white/10 focus:border-primary focus:outline-none font-body text-sm text-white"
                            placeholder="0.00"
                            data-testid="new-site-sales"
                          />
                        </div>
                      </>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="submit"
                      className="px-6 h-12 bg-primary text-white font-heading font-bold text-sm uppercase"
                      data-testid="submit-site-button"
                    >
                      CREATE SITE
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowCreateSite(false)}
                      className="px-6 h-12 bg-transparent border-2 border-white/20 text-white font-heading font-bold text-sm uppercase hover:border-white/40"
                      data-testid="cancel-site-button"
                    >
                      CANCEL
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Search Bar for Sites */}
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-white/40" strokeWidth={2} />
              <input
                type="text"
                value={siteSearchTerm}
                onChange={(e) => setSiteSearchTerm(e.target.value)}
                placeholder="Search sites by name, address or customer..."
                className="w-full h-12 pl-12 pr-4 bg-[#161920] border border-white/10 focus:border-primary focus:outline-none font-body text-sm text-white placeholder-white/40"
                data-testid="site-search-input"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filteredSites.map((site) => (
                <SiteCard
                  key={site.id}
                  site={site}
                  isEditing={editingSite?.id === site.id}
                  onEdit={(data) => setEditingSite(data)}
                  onSave={handleUpdateSite}
                  onCancelEdit={() => setEditingSite(null)}
                  onDelete={handleDeleteSite}
                  userRole={user?.role}
                />
              ))}
            </div>
          </div>
        )}

        {/* Jobs Tab */}
        {view === "jobs" && (
          <JobsTab
            jobs={jobs}
            unfinishedJobs={unfinishedJobs}
            drivers={drivers}
            sites={sites}
            filteredJobs={filteredJobs}
            expiredJobsCount={expiredJobsCount}
            archivedJobsCount={archivedJobs.length}
            showAddJob={showAddJob}
            setShowAddJob={setShowAddJob}
            newJob={newJob}
            setNewJob={setNewJob}
            jobFilter={jobFilter}
            setJobFilter={setJobFilter}
            editingJob={editingJob}
            setEditingJob={setEditingJob}
            handleCreateJob={handleCreateJob}
            handleDeleteJob={handleDeleteJob}
            handleUpdateJobStatus={handleUpdateJobStatus}
            handleEditJob={handleEditJob}
            handleSaveEditJob={handleSaveEditJob}
            handleArchiveJob={handleArchiveJob}
            handleUnarchiveJob={handleUnarchiveJob}
            handleArchiveAllExpired={handleArchiveAllExpired}
            fetchArchivedJobs={fetchArchivedJobs}
          />
        )}

        {view === "account" && (
          <div className="space-y-4">
            <div className="bg-[#161920] border border-white/10 p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="font-heading font-bold text-2xl uppercase text-white">MY ACCOUNT</h2>
                  <p className="text-sm text-white/60 mt-1">Manage your admin account settings</p>
                </div>
                {!editingAccount && (
                  <button
                    onClick={() => setEditingAccount(true)}
                    className="px-6 h-10 bg-primary text-white font-heading font-bold text-sm uppercase"
                    data-testid="edit-account-button"
                  >
                    EDIT ACCOUNT
                  </button>
                )}
              </div>

              {!editingAccount ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-semibold font-body text-white/60 uppercase mb-2">Full Name</label>
                    <p className="text-white font-body text-lg">{user.full_name}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold font-body text-white/60 uppercase mb-2">Username</label>
                    <p className="text-white font-mono text-lg">@{user.username}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold font-body text-white/60 uppercase mb-2">Role</label>
                    <p className="text-primary font-body text-lg uppercase font-bold">Administrator</p>
                  </div>
                </div>
              ) : (
                <AccountEditForm
                  user={user}
                  onSave={handleUpdateAccount}
                  onCancel={() => setEditingAccount(false)}
                />
              )}
            </div>
          </div>
        )}

        {view === "admins" && (
          <div className="space-y-4">
            <button
              onClick={() => setShowCreateAdmin(!showCreateAdmin)}
              className="flex items-center gap-2 px-6 h-12 bg-primary text-white font-heading font-bold uppercase shadow-[4px_4px_0px_0px_rgba(0,0,0,0.3)] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,0.3)] hover:translate-x-[2px] hover:translate-y-[2px] transition-all"
              data-testid="create-admin-button"
            >
              <UserPlus className="w-5 h-5" strokeWidth={2} />
              {showCreateAdmin ? "CANCEL" : "CREATE NEW ADMIN"}
            </button>

            {showCreateAdmin && (
              <div className="bg-[#161920] border border-white/10 p-6">
                <h3 className="font-heading font-bold text-lg uppercase text-white mb-4">NEW ADMIN</h3>
                <form onSubmit={handleCreateAdmin} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold font-body text-white/60 uppercase mb-2">Full Name</label>
                      <input
                        type="text"
                        value={newAdmin.full_name}
                        onChange={(e) => setNewAdmin({ ...newAdmin, full_name: e.target.value })}
                        required
                        className="w-full h-10 px-3 bg-transparent border border-white/20 focus:border-primary focus:outline-none text-white text-sm"
                        placeholder="Admin Name"
                        data-testid="new-admin-name"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold font-body text-white/60 uppercase mb-2">Username</label>
                      <input
                        type="text"
                        value={newAdmin.username}
                        onChange={(e) => setNewAdmin({ ...newAdmin, username: e.target.value })}
                        required
                        className="w-full h-10 px-3 bg-transparent border border-white/20 focus:border-primary focus:outline-none text-white text-sm"
                        placeholder="username"
                        data-testid="new-admin-username"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold font-body text-white/60 uppercase mb-2">Password</label>
                      <input
                        type="password"
                        value={newAdmin.password}
                        onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })}
                        required
                        className="w-full h-10 px-3 bg-transparent border border-white/20 focus:border-primary focus:outline-none text-white text-sm"
                        placeholder="••••••••"
                        data-testid="new-admin-password"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="submit"
                      className="px-6 h-10 bg-primary text-white font-heading font-bold text-sm uppercase"
                      data-testid="submit-new-admin"
                    >
                      CREATE ADMIN
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowCreateAdmin(false)}
                      className="px-6 h-10 bg-transparent border border-white/20 text-white/60 font-heading font-bold text-sm uppercase hover:border-white/40"
                    >
                      CANCEL
                    </button>
                  </div>
                </form>
              </div>
            )}

            <div className="grid grid-cols-1 gap-4">
              {filteredAdmins.map((admin) => (
                <div key={admin.id} className="bg-[#161920] border border-white/10 p-6" data-testid={`admin-card-${admin.id}`}>
                  {editingAdmin?.id === admin.id ? (
                    // Edit Mode
                    <div className="space-y-4">
                      <div>
                        <label className="block text-xs font-semibold font-body text-white/60 uppercase mb-2">Full Name</label>
                        <input
                          type="text"
                          value={editingAdmin.full_name}
                          onChange={(e) => setEditingAdmin({ ...editingAdmin, full_name: e.target.value })}
                          className="w-full h-10 px-3 bg-transparent border border-white/20 focus:border-primary focus:outline-none text-white text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold font-body text-white/60 uppercase mb-2">Username</label>
                        <input
                          type="text"
                          value={editingAdmin.username}
                          onChange={(e) => setEditingAdmin({ ...editingAdmin, username: e.target.value })}
                          className="w-full h-10 px-3 bg-transparent border border-white/20 focus:border-primary focus:outline-none text-white text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold font-body text-white/60 uppercase mb-2">New Password (leave blank to keep current)</label>
                        <input
                          type="password"
                          value={editingAdmin.password || ""}
                          onChange={(e) => setEditingAdmin({ ...editingAdmin, password: e.target.value })}
                          className="w-full h-10 px-3 bg-transparent border border-white/20 focus:border-primary focus:outline-none text-white text-sm"
                          placeholder="Enter new password..."
                        />
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            const updateData = { full_name: editingAdmin.full_name, username: editingAdmin.username };
                            if (editingAdmin.password) updateData.password = editingAdmin.password;
                            handleUpdateAdmin(admin.id, updateData);
                          }}
                          className="px-4 py-2 bg-primary text-white font-heading font-bold text-xs uppercase"
                        >
                          SAVE
                        </button>
                        <button
                          onClick={() => setEditingAdmin(null)}
                          className="px-4 py-2 bg-white/10 text-white font-heading font-bold text-xs uppercase"
                        >
                          CANCEL
                        </button>
                      </div>
                    </div>
                  ) : (
                    // View Mode
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-heading font-bold text-lg uppercase text-white">{admin.full_name}</h3>
                        <p className="font-mono text-sm text-white/60">@{admin.username}</p>
                        {admin.plain_password && (
                          <p className="font-mono text-sm text-accent-success mt-1">Password: {admin.plain_password}</p>
                        )}
                        <span className="inline-block mt-2 px-2 py-1 text-xs font-bold uppercase bg-primary/20 text-primary border border-primary/30">
                          ADMIN
                        </span>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setEditingAdmin({ id: admin.id, full_name: admin.full_name, username: admin.username, password: "" })}
                          className="p-2 bg-white/5 hover:bg-white/10 text-white"
                          data-testid={`edit-admin-button-${admin.id}`}
                          title="Edit admin"
                        >
                          <Edit2 className="w-4 h-4" strokeWidth={2} />
                        </button>
                        {admin.id !== user.id && (
                          <button
                            onClick={() => handleDeleteAdmin(admin.id)}
                            className="p-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 hover:text-red-300"
                            data-testid={`delete-admin-button-${admin.id}`}
                            title="Delete admin"
                          >
                            <Trash2 className="w-4 h-4" strokeWidth={2} />
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {filteredAdmins.length === 0 && (
                <div className="text-center py-8 text-white/40 font-body">No admins found</div>
              )}
            </div>
          </div>
        )}

        {/* Managers Tab - Admin Only */}
        {view === "managers" && user?.role === "admin" && (
          <div className="space-y-4">
            <button
              onClick={() => setShowAddManager(!showAddManager)}
              className="flex items-center gap-2 px-6 h-12 bg-primary text-white font-heading font-bold uppercase shadow-[4px_4px_0px_0px_rgba(0,0,0,0.3)] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,0.3)] hover:translate-x-[2px] hover:translate-y-[2px] transition-all"
              data-testid="create-manager-button"
            >
              <UserPlus className="w-5 h-5" strokeWidth={2} />
              {showAddManager ? "CANCEL" : "CREATE NEW MANAGER"}
            </button>

            {showAddManager && (
              <div className="bg-[#161920] border border-white/10 p-6">
                <h3 className="font-heading font-bold text-lg uppercase text-white mb-4">NEW MANAGER</h3>
                <form onSubmit={handleCreateManager} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold font-body text-white/60 uppercase mb-2">Full Name</label>
                      <input
                        type="text"
                        value={newManager.full_name}
                        onChange={(e) => setNewManager({ ...newManager, full_name: e.target.value })}
                        required
                        className="w-full h-10 px-3 bg-transparent border border-white/20 focus:border-primary focus:outline-none text-white text-sm"
                        placeholder="Manager Name"
                        data-testid="new-manager-name"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold font-body text-white/60 uppercase mb-2">Username</label>
                      <input
                        type="text"
                        value={newManager.username}
                        onChange={(e) => setNewManager({ ...newManager, username: e.target.value })}
                        required
                        className="w-full h-10 px-3 bg-transparent border border-white/20 focus:border-primary focus:outline-none text-white text-sm"
                        placeholder="username"
                        data-testid="new-manager-username"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold font-body text-white/60 uppercase mb-2">Password</label>
                      <input
                        type="password"
                        value={newManager.password}
                        onChange={(e) => setNewManager({ ...newManager, password: e.target.value })}
                        required
                        className="w-full h-10 px-3 bg-transparent border border-white/20 focus:border-primary focus:outline-none text-white text-sm"
                        placeholder="••••••••"
                        data-testid="new-manager-password"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="submit"
                      className="px-6 h-10 bg-primary text-white font-heading font-bold text-sm uppercase"
                      data-testid="submit-new-manager"
                    >
                      CREATE MANAGER
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowAddManager(false)}
                      className="px-6 h-10 bg-transparent border border-white/20 text-white/60 font-heading font-bold text-sm uppercase hover:border-white/40"
                    >
                      CANCEL
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Manager Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-white/40" strokeWidth={2} />
              <input
                type="text"
                value={managerSearchTerm}
                onChange={(e) => setManagerSearchTerm(e.target.value)}
                placeholder="Search managers..."
                className="w-full h-10 pl-10 pr-4 bg-transparent border border-white/20 focus:border-primary focus:outline-none text-white text-sm font-body"
                data-testid="manager-search"
              />
            </div>

            <div className="grid grid-cols-1 gap-4">
              {filteredManagers.map((manager) => (
                <div key={manager.id} className="bg-[#161920] border border-white/10 p-6" data-testid={`manager-card-${manager.id}`}>
                  {editingManager?.id === manager.id ? (
                    // Edit Mode
                    <div className="space-y-4">
                      <div>
                        <label className="block text-xs font-semibold font-body text-white/60 uppercase mb-2">Full Name</label>
                        <input
                          type="text"
                          value={editingManager.full_name}
                          onChange={(e) => setEditingManager({ ...editingManager, full_name: e.target.value })}
                          className="w-full h-10 px-3 bg-transparent border border-white/20 focus:border-primary focus:outline-none text-white text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold font-body text-white/60 uppercase mb-2">Username</label>
                        <input
                          type="text"
                          value={editingManager.username}
                          onChange={(e) => setEditingManager({ ...editingManager, username: e.target.value })}
                          className="w-full h-10 px-3 bg-transparent border border-white/20 focus:border-primary focus:outline-none text-white text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold font-body text-white/60 uppercase mb-2">New Password (leave blank to keep current)</label>
                        <input
                          type="password"
                          value={editingManager.password || ""}
                          onChange={(e) => setEditingManager({ ...editingManager, password: e.target.value })}
                          className="w-full h-10 px-3 bg-transparent border border-white/20 focus:border-primary focus:outline-none text-white text-sm"
                          placeholder="Enter new password..."
                        />
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            const updateData = { full_name: editingManager.full_name, username: editingManager.username };
                            if (editingManager.password) updateData.password = editingManager.password;
                            handleUpdateManager(manager.id, updateData);
                          }}
                          className="px-4 py-2 bg-primary text-white font-heading font-bold text-xs uppercase"
                        >
                          SAVE
                        </button>
                        <button
                          onClick={() => setEditingManager(null)}
                          className="px-4 py-2 bg-white/10 text-white font-heading font-bold text-xs uppercase"
                        >
                          CANCEL
                        </button>
                      </div>
                    </div>
                  ) : (
                    // View Mode
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-heading font-bold text-lg uppercase text-white">{manager.full_name}</h3>
                        <p className="font-mono text-sm text-white/60">@{manager.username}</p>
                        {manager.plain_password && (
                          <p className="font-mono text-sm text-accent-success mt-1">Password: {manager.plain_password}</p>
                        )}
                        <span className="inline-block mt-2 px-2 py-1 text-xs font-bold uppercase bg-accent-info/20 text-accent-info border border-accent-info/30">
                          MANAGER
                        </span>
                        {/* Show allowed tabs */}
                        <div className="mt-3">
                          <p className="text-xs font-semibold text-white/40 uppercase mb-1">Menu Access:</p>
                          <div className="flex flex-wrap gap-1">
                            {(manager.allowed_tabs || ["deliveries", "drivers", "sites", "jobs", "account"]).map(tab => (
                              <span key={tab} className="px-2 py-0.5 text-xs bg-white/5 text-white/60 border border-white/10">
                                {tab}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setEditingPermissions({ 
                            id: manager.id, 
                            full_name: manager.full_name,
                            allowed_tabs: manager.allowed_tabs || ["deliveries", "drivers", "sites", "jobs", "account"]
                          })}
                          className="p-2 bg-accent-info/10 hover:bg-accent-info/20 text-accent-info"
                          data-testid={`permissions-manager-button-${manager.id}`}
                          title="Edit menu permissions"
                        >
                          <Settings className="w-4 h-4" strokeWidth={2} />
                        </button>
                        <button
                          onClick={() => setEditingManager({ id: manager.id, full_name: manager.full_name, username: manager.username, password: "" })}
                          className="p-2 bg-white/5 hover:bg-white/10 text-white"
                          data-testid={`edit-manager-button-${manager.id}`}
                          title="Edit manager"
                        >
                          <Edit2 className="w-4 h-4" strokeWidth={2} />
                        </button>
                        <button
                          onClick={() => handleDeleteManager(manager.id)}
                          className="p-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 hover:text-red-300"
                          data-testid={`delete-manager-button-${manager.id}`}
                          title="Delete manager"
                        >
                          <Trash2 className="w-4 h-4" strokeWidth={2} />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {filteredManagers.length === 0 && (
                <div className="text-center py-8 text-white/40 font-body">No managers found</div>
              )}
            </div>
          </div>
        )}

        {/* Manager Permissions Modal */}
        {editingPermissions && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" data-testid="permissions-modal">
            <div className="bg-[#161920] border border-white/20 p-6 max-w-md w-full max-h-[80vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-heading font-bold text-lg uppercase text-white">
                  Menu Permissions
                </h3>
                <button
                  onClick={() => setEditingPermissions(null)}
                  className="p-2 hover:bg-white/10 text-white/60 hover:text-white"
                  data-testid="close-permissions-modal"
                >
                  <X className="w-5 h-5" strokeWidth={2} />
                </button>
              </div>
              
              <p className="text-sm text-white/60 mb-4">
                Configure which menu tabs <span className="text-white font-semibold">{editingPermissions.full_name}</span> can access:
              </p>
              
              <div className="space-y-2 mb-6">
                {ALL_CONFIGURABLE_TABS.map(tab => (
                  <label 
                    key={tab.id} 
                    className="flex items-center gap-3 p-3 bg-white/5 hover:bg-white/10 cursor-pointer transition-colors"
                    data-testid={`permission-checkbox-${tab.id}`}
                  >
                    <input
                      type="checkbox"
                      checked={editingPermissions.allowed_tabs.includes(tab.id)}
                      onChange={(e) => {
                        const newTabs = e.target.checked
                          ? [...editingPermissions.allowed_tabs, tab.id]
                          : editingPermissions.allowed_tabs.filter(t => t !== tab.id);
                        setEditingPermissions({ ...editingPermissions, allowed_tabs: newTabs });
                      }}
                      className="w-5 h-5 accent-primary cursor-pointer"
                    />
                    <span className="text-sm text-white font-body">{tab.label}</span>
                  </label>
                ))}
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => handleUpdatePermissions(editingPermissions.id, editingPermissions.allowed_tabs)}
                  className="flex-1 h-10 bg-primary text-white font-heading font-bold text-sm uppercase flex items-center justify-center gap-2"
                  data-testid="save-permissions-button"
                >
                  <Check className="w-4 h-4" strokeWidth={2} />
                  SAVE PERMISSIONS
                </button>
                <button
                  onClick={() => setEditingPermissions(null)}
                  className="px-4 h-10 bg-white/10 text-white font-heading font-bold text-sm uppercase"
                >
                  CANCEL
                </button>
              </div>
            </div>
          </div>
        )}

        {view === "analytics" && hasTabAccess("analytics") && (
          <AnalyticsTab drivers={drivers} sites={sites} />
        )}

        {/* Financials Tab */}
        {view === "financials" && hasTabAccess("financials") && (
          <FinancialsTab drivers={drivers} sites={sites} />
        )}

        {/* Images Gallery View */}
        {view === "images" && (
          <div className="space-y-4">
            {/* Action Bar */}
            <div className="bg-[#161920] border border-white/10 p-4">
              <div className="flex flex-wrap items-center gap-2 sm:gap-4">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={paginatedImages.length > 0 && paginatedImages.every(img => selectedImages.includes(img.id))}
                    onChange={selectAllImages}
                    className="w-5 h-5 accent-primary cursor-pointer"
                    data-testid="select-all-images"
                  />
                  <span className="text-sm text-white/60 font-body">Select All</span>
                </div>
                
                <div className="text-sm text-white/60 font-body">
                  {selectedImages.length} selected
                </div>

                <button
                  onClick={downloadSelectedImages}
                  disabled={selectedImages.length === 0}
                  className="px-3 h-10 flex items-center gap-2 bg-accent-success/10 hover:bg-accent-success/20 border border-accent-success/30 text-accent-success transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="download-selected-images"
                >
                  <Download className="w-4 h-4" strokeWidth={2} />
                  <span className="font-heading font-bold text-xs uppercase">DOWNLOAD</span>
                </button>

                <button
                  onClick={deleteSelectedImages}
                  disabled={selectedImages.length === 0}
                  className="px-3 h-10 flex items-center gap-2 bg-accent-error/10 hover:bg-accent-error/20 border border-accent-error/30 text-accent-error transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="delete-selected-images"
                >
                  <Trash2 className="w-4 h-4" strokeWidth={2} />
                  <span className="font-heading font-bold text-xs uppercase">DELETE</span>
                </button>

                <button
                  onClick={deleteAllImages}
                  disabled={allImages.length === 0}
                  className="px-3 h-10 flex items-center gap-2 bg-red-600/20 hover:bg-red-600/30 border border-red-600/50 text-red-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="delete-all-images"
                >
                  <Trash2 className="w-4 h-4" strokeWidth={2} />
                  <span className="font-heading font-bold text-xs uppercase">DELETE ALL</span>
                </button>

                <button
                  onClick={fetchImages}
                  className="px-3 h-10 flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/20 text-white transition-colors ml-auto"
                  data-testid="refresh-images"
                >
                  <span className="font-heading font-bold text-xs uppercase">REFRESH</span>
                </button>
              </div>
            </div>

            {/* Images Grid */}
            {imagesLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-white/60 font-body">Loading images...</p>
                </div>
              </div>
            ) : paginatedImages.length === 0 ? (
              <div className="bg-[#161920] border border-white/10 p-12 text-center">
                <ImageIcon className="w-12 h-12 text-white/20 mx-auto mb-4" strokeWidth={1.5} />
                <p className="text-white/60 font-body">No images found</p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2">
                  {paginatedImages.map((img) => (
                    <div 
                      key={img.id} 
                      className={`relative group cursor-pointer border-2 transition-all ${
                        selectedImages.includes(img.id) 
                          ? 'border-primary' 
                          : 'border-transparent hover:border-white/30'
                      }`}
                      onClick={() => toggleImageSelection(img.id)}
                      data-testid={`image-card-${img.id}`}
                    >
                      <div className="aspect-square bg-[#161920] overflow-hidden">
                        <img 
                          src={img.photo} 
                          alt={`Delivery at ${img.site_name}`}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      </div>
                      
                      {/* Selection checkbox overlay */}
                      <div className={`absolute top-2 left-2 w-6 h-6 rounded-sm flex items-center justify-center transition-all ${
                        selectedImages.includes(img.id)
                          ? 'bg-primary'
                          : 'bg-black/50 group-hover:bg-black/70'
                      }`}>
                        {selectedImages.includes(img.id) && (
                          <CheckCircle className="w-4 h-4 text-white" strokeWidth={2} />
                        )}
                      </div>

                      {/* Info overlay on hover */}
                      <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-2">
                        <p className="text-xs text-white font-bold truncate">{img.site_name}</p>
                        <p className="text-xs text-white/70 truncate">{img.driver_name}</p>
                        <p className="text-xs text-white/50">{formatDateDMY(img.checkin_time)}</p>
                      </div>

                      {/* View full button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedPhoto(img);
                        }}
                        className="absolute top-2 right-2 w-6 h-6 bg-black/50 hover:bg-black/70 rounded-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <FileText className="w-3 h-3 text-white" strokeWidth={2} />
                      </button>
                    </div>
                  ))}
                </div>

                {/* Pagination */}
                {totalImagePages > 1 && (
                  <div className="flex items-center justify-center gap-2 py-4">
                    <button
                      onClick={() => setImagesPage(p => Math.max(1, p - 1))}
                      disabled={imagesPage === 1}
                      className="px-3 py-2 bg-[#161920] border border-white/10 text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white/5 transition-colors"
                    >
                      <ChevronLeft className="w-4 h-4" strokeWidth={2} />
                    </button>
                    <span className="text-sm text-white/60 font-mono px-4">
                      Page {imagesPage} of {totalImagePages} ({allImages.length} images)
                    </span>
                    <button
                      onClick={() => setImagesPage(p => Math.min(totalImagePages, p + 1))}
                      disabled={imagesPage === totalImagePages}
                      className="px-3 py-2 bg-[#161920] border border-white/10 text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white/5 transition-colors"
                    >
                      <ChevronRight className="w-4 h-4" strokeWidth={2} />
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
        </>
        )}
      </main>

      {/* Photo Modal */}
      {selectedPhoto && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" 
          onClick={() => setSelectedPhoto(null)}
          data-testid="photo-modal"
        >
          <div 
            className="bg-[#161920] border border-white/10 max-w-4xl w-full max-h-[90vh] overflow-auto" 
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b border-white/10 flex items-center justify-between">
              <div>
                <h3 className="font-heading font-bold text-lg uppercase text-white">DELIVERY PHOTO</h3>
                <p className="text-sm text-white/60 mt-1">
                  {selectedPhoto.site_name} • {selectedPhoto.driver_name}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => downloadPhoto(selectedPhoto)}
                  className="px-4 py-2 bg-primary hover:bg-primary/80 text-white font-heading font-bold text-xs uppercase transition-colors flex items-center gap-2"
                  data-testid="download-photo-button"
                >
                  <Download className="w-4 h-4" strokeWidth={2} />
                  DOWNLOAD
                </button>
                <button
                  onClick={() => setSelectedPhoto(null)}
                  className="p-2 hover:bg-white/10 text-white transition-colors"
                  data-testid="close-photo-modal"
                >
                  <X className="w-5 h-5" strokeWidth={2} />
                </button>
              </div>
            </div>
            <div className="p-6">
              {selectedPhoto.loading ? (
                <div className="w-full h-64 flex items-center justify-center bg-white/5 border border-white/10">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
                    <p className="text-white/60 text-sm">Loading photo...</p>
                  </div>
                </div>
              ) : selectedPhoto.photo ? (
                <img 
                  src={selectedPhoto.photo} 
                  alt="Delivery proof" 
                  className="w-full h-auto border border-white/10"
                  data-testid="delivery-photo"
                />
              ) : (
                <div className="w-full h-64 flex items-center justify-center bg-white/5 border border-white/10">
                  <p className="text-white/40">Photo not available</p>
                </div>
              )}
              <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-white/60 uppercase text-xs font-semibold mb-1">Check-in Time</p>
                  <p className="text-white font-mono">{formatDateTimeDMY(selectedPhoto.checkin_time)}</p>
                </div>
                <div>
                  <p className="text-white/60 uppercase text-xs font-semibold mb-1">Check-out Time</p>
                  <p className="text-white font-mono">
                    {selectedPhoto.checkout_time ? formatDateTimeDMY(selectedPhoto.checkout_time) : "In Progress"}
                  </p>
                </div>
                <div>
                  <p className="text-white/60 uppercase text-xs font-semibold mb-1">Truck</p>
                  <p className="text-white font-mono">{selectedPhoto.truck_number}</p>
                </div>
                <div>
                  <p className="text-white/60 uppercase text-xs font-semibold mb-1">Status</p>
                  <p className="text-white uppercase font-bold">{selectedPhoto.status}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

        {/* Maintenance Tab (Repairs, Mechanics, Inventory, Purchasing, Suppliers) */}
        {view === "maintenance" && (
          <MaintenanceTab drivers={drivers} user={user} />
        )}

        {/* Dumping Grounds Tab */}
        {view === "dumping" && (
          <DumpingGroundsTab />
        )}

        {/* Projects Tab */}
        {view === "projects" && hasTabAccess("projects") && (
          <ProjectsTab />
        )}
    </div>
  );
}

function AccountEditForm({ user, onSave, onCancel }) {
  const [formData, setFormData] = useState({
    full_name: user.full_name,
    username: user.username,
    password: "",
    confirm_password: ""
  });
  const [errors, setErrors] = useState({});

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = {};

    if (formData.password) {
      if (formData.password.length < 6) {
        newErrors.password = "Password must be at least 6 characters";
      }
      if (formData.password !== formData.confirm_password) {
        newErrors.confirm_password = "Passwords do not match";
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const updateData = {
      full_name: formData.full_name,
      username: formData.username
    };

    if (formData.password) {
      updateData.password = formData.password;
    }

    onSave(updateData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-semibold font-body text-white/80 mb-2">FULL NAME</label>
          <input
            type="text"
            value={formData.full_name}
            onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
            required
            className="w-full h-12 px-4 bg-transparent border-2 border-white/10 focus:border-primary focus:outline-none font-body text-sm text-white"
            data-testid="account-fullname-input"
          />
        </div>
        <div>
          <label className="block text-sm font-semibold font-body text-white/80 mb-2">USERNAME</label>
          <input
            type="text"
            value={formData.username}
            onChange={(e) => setFormData({ ...formData, username: e.target.value })}
            required
            className="w-full h-12 px-4 bg-transparent border-2 border-white/10 focus:border-primary focus:outline-none font-mono text-sm text-white"
            data-testid="account-username-input"
          />
        </div>
      </div>

      <div className="border-t border-white/10 pt-4 mt-4">
        <p className="text-sm text-white/60 mb-4">Leave password fields empty to keep current password</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold font-body text-white/80 mb-2">NEW PASSWORD</label>
            <input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              className="w-full h-12 px-4 bg-transparent border-2 border-white/10 focus:border-primary focus:outline-none font-mono text-sm text-white"
              placeholder="Leave blank to keep current"
              data-testid="account-password-input"
            />
            {errors.password && <p className="text-red-400 text-xs mt-1">{errors.password}</p>}
          </div>
          <div>
            <label className="block text-sm font-semibold font-body text-white/80 mb-2">CONFIRM PASSWORD</label>
            <input
              type="password"
              value={formData.confirm_password}
              onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
              className="w-full h-12 px-4 bg-transparent border-2 border-white/10 focus:border-primary focus:outline-none font-mono text-sm text-white"
              placeholder="Confirm new password"
              data-testid="account-confirm-password-input"
            />
            {errors.confirm_password && <p className="text-red-400 text-xs mt-1">{errors.confirm_password}</p>}
          </div>
        </div>
      </div>

      <div className="flex gap-2">
        <button
          type="submit"
          className="px-6 h-12 bg-primary text-white font-heading font-bold text-sm uppercase"
          data-testid="save-account-button"
        >
          SAVE CHANGES
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-6 h-12 bg-transparent border-2 border-white/20 text-white font-heading font-bold text-sm uppercase hover:border-white/40"
          data-testid="cancel-account-button"
        >
          CANCEL
        </button>
      </div>
    </form>
  );
}

function DriverCard({ driver, sites, isEditing, isAssigning, onEdit, onSave, onCancelEdit, onAssign, onSaveAssignment, onDelete }) {
  const [editData, setEditData] = useState({
    full_name: driver.full_name,
    username: driver.username,
    truck_number: driver.truck_number || "",
    phone: driver.phone || "",
    password: "",
    confirm_password: ""
  });
  const [errors, setErrors] = useState({});

  const handleSave = () => {
    const newErrors = {};

    if (editData.password) {
      if (editData.password.length < 6) {
        newErrors.password = "Password must be at least 6 characters";
        setErrors(newErrors);
        return;
      }
      if (editData.password !== editData.confirm_password) {
        newErrors.confirm_password = "Passwords do not match";
        setErrors(newErrors);
        return;
      }
    }

    const updateData = {
      full_name: editData.full_name,
      username: editData.username,
      truck_number: editData.truck_number,
      phone: editData.phone
    };

    if (editData.password) {
      updateData.password = editData.password;
    }

    onSave(driver.id, updateData);
  };

  if (isEditing) {
    return (
      <div className="bg-[#161920] border border-white/10 p-6" data-testid={`driver-card-${driver.id}`}>
        <h3 className="font-heading font-bold text-lg uppercase text-white mb-4">EDIT DRIVER</h3>
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-semibold font-body text-white/80 mb-2">FULL NAME</label>
            <input
              type="text"
              value={editData.full_name}
              onChange={(e) => setEditData({ ...editData, full_name: e.target.value })}
              className="w-full h-12 px-4 bg-transparent border-2 border-white/10 focus:border-primary focus:outline-none font-body text-sm text-white"
              data-testid={`edit-driver-name-${driver.id}`}
            />
          </div>
          <div>
            <label className="block text-sm font-semibold font-body text-white/80 mb-2">USERNAME</label>
            <input
              type="text"
              value={editData.username}
              onChange={(e) => setEditData({ ...editData, username: e.target.value })}
              className="w-full h-12 px-4 bg-transparent border-2 border-white/10 focus:border-primary focus:outline-none font-mono text-sm text-white"
              data-testid={`edit-driver-username-${driver.id}`}
            />
          </div>
          <div>
            <label className="block text-sm font-semibold font-body text-white/80 mb-2">TRUCK NUMBER</label>
            <input
              type="text"
              value={editData.truck_number}
              onChange={(e) => setEditData({ ...editData, truck_number: e.target.value })}
              className="w-full h-12 px-4 bg-transparent border-2 border-white/10 focus:border-primary focus:outline-none font-mono text-sm text-white"
              data-testid={`edit-driver-truck-${driver.id}`}
            />
          </div>
          <div>
            <label className="block text-sm font-semibold font-body text-white/80 mb-2">WHATSAPP NUMBER</label>
            <input
              type="tel"
              value={editData.phone}
              onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
              placeholder="+65 9123 4567"
              className="w-full h-12 px-4 bg-transparent border-2 border-white/10 focus:border-primary focus:outline-none font-mono text-sm text-white"
              data-testid={`edit-driver-phone-${driver.id}`}
            />
          </div>
        </div>
        <div className="border-t border-white/10 pt-4 mb-4">
          <p className="text-sm text-white/60 mb-4">Change driver password (leave blank to keep current)</p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold font-body text-white/80 mb-2">NEW PASSWORD</label>
              <input
                type="password"
                value={editData.password}
                onChange={(e) => setEditData({ ...editData, password: e.target.value })}
                className="w-full h-12 px-4 bg-transparent border-2 border-white/10 focus:border-primary focus:outline-none font-mono text-sm text-white"
                placeholder="Leave blank to keep current"
                data-testid={`edit-driver-password-${driver.id}`}
              />
              {errors.password && <p className="text-red-400 text-xs mt-1">{errors.password}</p>}
            </div>
            <div>
              <label className="block text-sm font-semibold font-body text-white/80 mb-2">CONFIRM PASSWORD</label>
              <input
                type="password"
                value={editData.confirm_password}
                onChange={(e) => setEditData({ ...editData, confirm_password: e.target.value })}
                className="w-full h-12 px-4 bg-transparent border-2 border-white/10 focus:border-primary focus:outline-none font-mono text-sm text-white"
                placeholder="Confirm new password"
                data-testid={`edit-driver-confirm-password-${driver.id}`}
              />
              {errors.confirm_password && <p className="text-red-400 text-xs mt-1">{errors.confirm_password}</p>}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleSave}
            className="px-6 h-10 bg-primary text-white font-heading font-bold text-xs uppercase"
            data-testid={`save-driver-${driver.id}`}
          >
            SAVE
          </button>
          <button
            onClick={onCancelEdit}
            className="px-6 h-10 bg-transparent border-2 border-white/20 text-white font-heading font-bold text-xs uppercase"
            data-testid={`cancel-edit-driver-${driver.id}`}
          >
            CANCEL
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#161920] border border-white/10 p-6" data-testid={`driver-card-${driver.id}`}>
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="font-heading font-bold text-lg uppercase text-white">{driver.full_name}</h3>
          <p className="font-mono text-sm text-white/60">@{driver.username}</p>
          {driver.truck_number && (
            <p className="font-mono text-sm text-white/60 mt-1">Truck: {driver.truck_number}</p>
          )}
          {driver.phone && (
            <p className="font-mono text-sm text-white/60 mt-1">WhatsApp: {driver.phone}</p>
          )}
          {driver.plain_password && (
            <p className="font-mono text-sm text-accent-success mt-1">Password: {driver.plain_password}</p>
          )}
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => onEdit({ id: driver.id, full_name: driver.full_name, username: driver.username, truck_number: driver.truck_number || "", phone: driver.phone || "" })}
            className="p-2 bg-white/5 hover:bg-white/10 text-white"
            data-testid={`edit-driver-button-${driver.id}`}
          >
            <Edit2 className="w-4 h-4" strokeWidth={2} />
          </button>
          <button
            onClick={() => onDelete(driver.id)}
            className="p-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 hover:text-red-300"
            data-testid={`delete-driver-button-${driver.id}`}
            title="Delete driver"
          >
            <Trash2 className="w-4 h-4" strokeWidth={2} />
          </button>
          <button
            onClick={() => onAssign(driver.id)}
            className="px-4 py-2 bg-primary text-white font-heading font-bold text-xs uppercase"
            data-testid={`assign-sites-button-${driver.id}`}
          >
            {isAssigning ? "CANCEL" : "ASSIGN SITES"}
          </button>
        </div>
      </div>

      {driver.assigned_sites && driver.assigned_sites.length > 0 && (
        <div className="mb-4">
          <p className="text-xs font-semibold font-body text-white/60 uppercase mb-2">Assigned Sites ({driver.assigned_sites.length})</p>
          <div className="flex flex-wrap gap-2">
            {driver.assigned_sites.map((siteId) => {
              const site = sites.find(s => s.id === siteId);
              return site ? (
                <span key={siteId} className="px-2 py-1 bg-white/5 border border-white/10 text-xs text-white font-body">
                  {site.name}{site.customer_name ? ` (${site.customer_name})` : ''}
                </span>
              ) : null;
            })}
          </div>
        </div>
      )}

      {isAssigning && (
        <div className="mt-4 pt-4 border-t border-white/10">
          <p className="text-sm font-semibold font-body text-white/80 mb-3">SELECT SITES TO ASSIGN</p>
          <SiteAssignment
            sites={sites}
            assignedSites={driver.assigned_sites || []}
            onAssign={(siteIds) => onSaveAssignment(driver.id, siteIds)}
          />
        </div>
      )}
    </div>
  );
}

function SiteCard({ site, isEditing, onEdit, onSave, onCancelEdit, onDelete, userRole }) {
  const [editData, setEditData] = useState({
    name: site.name,
    address: site.address,
    customer_name: site.customer_name || "",
    cost: site.cost || "",
    sales: site.sales || ""
  });

  if (isEditing) {
    return (
      <div className="bg-[#161920] border border-white/10 p-6" data-testid={`site-card-${site.id}`}>
        <h3 className="font-heading font-bold text-lg uppercase text-white mb-4">EDIT SITE</h3>
        <div className="space-y-4 mb-4">
          <div>
            <label className="block text-sm font-semibold font-body text-white/80 mb-2">CUSTOMER NAME</label>
            <input
              type="text"
              value={editData.customer_name}
              onChange={(e) => setEditData({ ...editData, customer_name: e.target.value })}
              className="w-full h-12 px-4 bg-transparent border-2 border-white/10 focus:border-primary focus:outline-none font-body text-sm text-white"
              data-testid={`edit-site-customer-${site.id}`}
            />
          </div>
          <div>
            <label className="block text-sm font-semibold font-body text-white/80 mb-2 flex items-center">
              SITE NAME
              <span
                className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full bg-white/10 text-white/60 text-[10px] cursor-help"
                title="Any ( ) or - in the name will be replaced with a space when saved (WhatsApp Channel Policy)."
              >
                ?
              </span>
            </label>
            <input
              type="text"
              value={editData.name}
              onChange={(e) => setEditData({ ...editData, name: e.target.value })}
              className="w-full h-12 px-4 bg-transparent border-2 border-white/10 focus:border-primary focus:outline-none font-body text-sm text-white"
              data-testid={`edit-site-name-${site.id}`}
            />
            {(editData.name.includes("(") || editData.name.includes(")") || editData.name.includes("-")) && (
              <p className="text-xs text-yellow-400 mt-1">Note: ( ), - will be replaced with spaces on save.</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-semibold font-body text-white/80 mb-2">ADDRESS</label>
            <input
              type="text"
              value={editData.address}
              onChange={(e) => setEditData({ ...editData, address: e.target.value })}
              className="w-full h-12 px-4 bg-transparent border-2 border-white/10 focus:border-primary focus:outline-none font-body text-sm text-white"
              data-testid={`edit-site-address-${site.id}`}
            />
          </div>
          {/* Cost/Sales fields - Admin and Manager */}
          {(userRole === "admin" || userRole === "manager") && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold font-body text-white/80 mb-2">COST PER LOAD ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editData.cost}
                    onChange={(e) => setEditData({ ...editData, cost: e.target.value })}
                    className="w-full h-12 px-4 bg-transparent border-2 border-white/10 focus:border-primary focus:outline-none font-body text-sm text-white"
                    placeholder="0.00"
                    data-testid={`edit-site-cost-${site.id}`}
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold font-body text-white/80 mb-2">SALES PER LOAD ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editData.sales}
                    onChange={(e) => setEditData({ ...editData, sales: e.target.value })}
                    className="w-full h-12 px-4 bg-transparent border-2 border-white/10 focus:border-primary focus:outline-none font-body text-sm text-white"
                    placeholder="0.00"
                    data-testid={`edit-site-sales-${site.id}`}
                  />
                </div>
              </div>
            </>
          )}
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => {
              const saveData = {
                name: editData.name,
                address: editData.address,
                customer_name: editData.customer_name
              };
              if (userRole === "admin") {
                saveData.cost = editData.cost ? parseFloat(editData.cost) : null;
                saveData.sales = editData.sales ? parseFloat(editData.sales) : null;
              }
              onSave(site.id, saveData);
            }}
            className="px-6 h-10 bg-primary text-white font-heading font-bold text-xs uppercase"
            data-testid={`save-site-${site.id}`}
          >
            SAVE
          </button>
          <button
            onClick={onCancelEdit}
            className="px-6 h-10 bg-transparent border-2 border-white/20 text-white font-heading font-bold text-xs uppercase"
            data-testid={`cancel-edit-site-${site.id}`}
          >
            CANCEL
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#161920] border border-white/10 p-6" data-testid={`site-card-${site.id}`}>
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-start gap-2 flex-1">
          <MapPin className="w-5 h-5 text-primary mt-1 flex-shrink-0" strokeWidth={2} />
          <div className="min-w-0">
            {site.customer_name && (
              <p className="text-xs text-white/60 uppercase tracking-wide mb-1">{site.customer_name}</p>
            )}
            <h3 className="font-heading font-bold text-lg uppercase text-white">{site.name}</h3>
            <p className="text-sm text-white/60">{site.address}</p>
            {/* Cost/Sales display - Admin and Manager */}
            {(userRole === "admin" || userRole === "manager") && (site.cost !== undefined || site.sales !== undefined) && (
              <div className="flex gap-4 mt-2">
                {site.cost !== undefined && site.cost !== null && (
                  <span className="text-xs text-red-400">Cost/Load: ${Number(site.cost).toLocaleString()}</span>
                )}
                {site.sales !== undefined && site.sales !== null && (
                  <span className="text-xs text-green-400">Sales/Load: ${Number(site.sales).toLocaleString()}</span>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => onEdit({ 
              id: site.id, 
              name: site.name, 
              address: site.address, 
              customer_name: site.customer_name || "",
              cost: site.cost || "",
              sales: site.sales || ""
            })}
            className="p-2 bg-white/5 hover:bg-white/10 text-white"
            data-testid={`edit-site-button-${site.id}`}
          >
            <Edit2 className="w-4 h-4" strokeWidth={2} />
          </button>
          <button
            onClick={() => onDelete(site.id)}
            className="p-2 bg-red-500/10 hover:bg-red-500/20 text-red-400"
            data-testid={`delete-site-button-${site.id}`}
          >
            <Trash2 className="w-4 h-4" strokeWidth={2} />
          </button>
        </div>
      </div>
    </div>
  );
}

function SiteAssignment({ sites, assignedSites, onAssign }) {
  const [selectedSites, setSelectedSites] = useState(assignedSites);

  const toggleSite = (siteId) => {
    if (selectedSites.includes(siteId)) {
      setSelectedSites(selectedSites.filter(id => id !== siteId));
    } else {
      setSelectedSites([...selectedSites, siteId]);
    }
  };

  // Sort sites alphabetically by name
  const sortedSites = [...sites].sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-64 overflow-y-auto">
        {sortedSites.map((site) => (
          <button
            key={site.id}
            onClick={() => toggleSite(site.id)}
            className={`p-3 text-left border transition-all ${
              selectedSites.includes(site.id)
                ? "border-primary bg-primary/10"
                : "border-white/10 hover:border-white/20"
            }`}
            data-testid={`site-checkbox-${site.id}`}
          >
            <div className="flex items-start gap-2">
              <MapPin className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" strokeWidth={2} />
              <div className="min-w-0">
                <p className="font-body text-sm text-white break-words">{site.name}</p>
                {site.customer_name && (
                  <p className="font-body text-xs text-white/50 break-words">{site.customer_name}</p>
                )}
              </div>
            </div>
          </button>
        ))}
      </div>
      <button
        onClick={() => onAssign(selectedSites)}
        className="w-full h-12 bg-primary text-white font-heading font-bold text-sm uppercase"
        data-testid="save-sites-button"
      >
        SAVE ASSIGNED SITES
      </button>
    </div>
  );
}
